import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Grid, CircularProgress,
  Alert, Chip, Divider, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, LinearProgress, Table, TableHead, TableBody,
  TableRow, TableCell, Card, CardContent,
  Autocomplete, Checkbox, FormControlLabel, Radio, Switch
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  DragIndicator as DragIcon,
  Calculate as CalculateIcon,
  ContentCopy as ContentCopyIcon,
  LocalParking as ParkingIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as PreviewIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { telefoonNl } from '../../utils/telefoon';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { leesJson, meldFout } from '../../utils/apiFout';
import DuplicateCallsheetDialog from './DuplicateCallsheetDialog';
import ReistijdRegel from './ReistijdRegel';
import { useCallsheetFotos, FotoStrook, PlanningFotoCel, WordExportKnop } from './CallsheetFotos';
import CallsheetBijlagen, { aantalBijlagen, BijlagenInDialoog, bijlagenProbleem, informeerCrew } from './CallsheetBijlagen';
import CallsheetVerzendCheck, { feitenUitCallsheet, zelfdeOntvangers } from './CallsheetVerzendCheck';
import CallsheetTekstEditor, { OpmaakVeld } from './CallsheetTekstEditor';
import { correctiesVoor, metRegeleinden } from './callsheetMarkdown';
import { slaVeldOp } from './callsheetOpslaan';
import { useMsal } from '@azure/msal-react';
import { createDraftMessage } from '../../utils/graphMailSearch';
import { stripBronRefs, bronRefsNaarLinks, mdLinkComponents, standaardConflictKeuze } from '../../utils/briefingBronnen';
import useAiVoorbereiden from './useAiVoorbereiden';
import AiVoortgang from './AiVoortgang';
import { crewMetDubbelen } from '../../utils/crewDubbelen';
import KlantVereistenBlok from '../../ui-components/KlantVereistenBlok';
import { huidigeBlokken, leegBijStart, kiesInvulling, blokVanVeld, vulBlokken, maakControle, biedDeelOngedaanAan, voorstelUitJob, herstelBlok, naHerstelBijwerken } from './aiVoorbereiden';

// Dubbele crew (wizard + Airtable-automation, 21 okt) niet tonen. Elke getoonde regel draagt de ids van zijn
// verborgen kopieën (`_dubbelen`) mee, zodat verwijderen/vervangen die ook opruimt en ze niet terugkomen.
const metUniekeCrew = (d) => (d && Array.isArray(d.crew) ? { ...d, crew: crewMetDubbelen(d.crew) } : d);
const verwijderCrewDubbelen = async (ids) => {
  const uitkomst = await Promise.all((ids || []).map((dubbelId) =>
    authFetch(`${BACKEND_URL}/Api/callsheet-crew/${dubbelId}`, { method: 'DELETE' }).then((r) => r.ok, () => false)));
  const mislukt = uitkomst.filter((ok) => !ok).length;
  if (mislukt) throw new Error(`${mislukt} verborgen dubbele crewregel(s) niet verwijderd — probeer het opnieuw`);
};

// Function options for crew
const FUNCTIE_OPTIONS = [
  'Regisseur', 'Opnameleider', 'Camera Operator', 'Cinematograaf / DOP',
  'Camera Assistent', '1e Camera Assistent', '2e Camera Assistent',
  'Fotograaf', 'Fotograaf Assistent', 'Gaffer', 'Belichtingsassistent / Best Boy',
  'Geluidstechnicus', 'Geluid Assistent', 'Producer', 'Productie Assistent',
  'Art Director', 'Setdresser', 'Visagist', 'Stylist', 'Food Stylist',
  'Editor', 'Opdrachtgever', 'Assistent'
];

// Format duration (seconds) to HH:MM
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Parse HH:MM to seconds
const parseDuration = (str) => {
  if (!str) return null;
  const [hours, minutes] = str.split(':').map(Number);
  return (hours || 0) * 3600 + (minutes || 0) * 60;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('nl-NL', { 
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
    });
  } catch { return dateStr; }
};

// ============ CREW DIALOG ============
// Crewrij zonder koppeling (naam-snapshot) → persoon uit de crew-pool op exacte naam.
export function persoonOpNaam(naam, pool = []) {
  const n = String(naam || '').trim().toLowerCase();
  if (!n) return null;
  return pool.find((p) => String(p.fields?.['Volledige naam'] || p.fields?.Naam || '').trim().toLowerCase() === n) || null;
}

function CrewDialog({ open, onClose, onSave, initialData, callsheetId, personen = [] }) {
  const [selectedPersoon, setSelectedPersoon] = useState(null);
  const [persoon, setPersoon] = useState('');
  const [functie, setFunctie] = useState('');
  const [calltijd, setCalltijd] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [saving, setSaving] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);

  // Maak een nieuw, herbruikbaar Personen-record aan en koppel het meteen.
  const createCrewContact = async () => {
    const nm = (window.prompt('Naam van het nieuwe crew-contact:', persoon || '') || '').trim();
    if (!nm) return;
    setCreatingContact(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-new-crew-contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naam: nm, telefoon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSelectedPersoon({ id: data.id, fields: { Naam: data.naam, 'Volledige naam': data.naam } });
      setPersoon(data.naam);
      toast.success(`Contact "${data.naam}" aangemaakt`);
    } catch (err) {
      toast.error(`Aanmaken mislukt: ${err.message}`);
    } finally {
      setCreatingContact(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      setPersoon(initialData.fields?.Persoon || '');
      setFunctie(initialData.fields?.['Functie (handmatig)'] || '');
      setCalltijd(formatDuration(initialData.fields?.Calltijd));
      setTelefoon(initialData.fields?.['Telefoon (handmatig)'] || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
      // Try to find the linked person. Freelancers uit de Leveranciers-base zijn niet
      // koppelbaar en staan als naam-snapshot; herken ze op naam, anders opende de
      // rij als losse tekst (wiki-feedback Callsheets 26 sep: "Falco werd niet als
      // freelancer overgenomen maar als tekst").
      const linkedPersonId = initialData.fields?.Personen?.[0];
      const found = linkedPersonId
        ? personen.find(p => p.id === linkedPersonId)
        : persoonOpNaam(initialData.fields?.Persoon, personen);
      setSelectedPersoon(found || null);
      setUseManual(!found);
      // Show advanced fields if editing or if any advanced field has data
      setShowAdvanced(true);
    } else {
      setSelectedPersoon(null);
      setPersoon('');
      setFunctie('');
      setCalltijd('');
      setTelefoon('');
      setOpmerkingen('');
      setUseManual(false);
      setShowAdvanced(false); // Hide advanced fields for new entries
    }
  }, [initialData, open, personen]);

  const handlePersonSelect = (event, newValue) => {
    setSelectedPersoon(newValue);
    if (newValue) {
      setPersoon(newValue.fields?.['Volledige naam'] || newValue.fields?.Naam || '');
      // Auto-fill telefoonnummer uit de gekozen persoon.
      const tel = newValue.fields?.Telefoonnummer || '';
      if (tel) setTelefoon(Array.isArray(tel) ? tel[0] : String(tel));
      // Auto-fill functie if available
      const functies = newValue.fields?.Functie;
      if (functies && functies.length > 0) {
        const functieArr = Array.isArray(functies) ? functies : [functies];
        // Try to match with our FUNCTIE_OPTIONS
        const matched = FUNCTIE_OPTIONS.find(f =>
          functieArr.some(cf => String(cf).toLowerCase().includes(f.toLowerCase()) || f.toLowerCase().includes(String(cf).toLowerCase()))
        );
        // Vul altijd in: matchende standaardfunctie, anders de ruwe functie van de persoon.
        setFunctie(matched || String(functieArr[0] || ''));
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        persoon: selectedPersoon ? (selectedPersoon.fields?.['Volledige naam'] || selectedPersoon.fields?.Naam || persoon) : persoon,
        // Leveranciers-base personen (_source) komen uit een andere Airtable-base en kunnen
        // NIET als linked record worden opgeslagen → bewaar als snapshot (naam+functie+tel).
        personenId: (selectedPersoon && !selectedPersoon._source) ? selectedPersoon.id : null,
        functie,
        calltijd: parseDuration(calltijd),
        telefoon,
        opmerkingen,
        callsheetId
      };

      if (initialData?.id) {
        // Update existing
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-crew/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Persoon': data.persoon,
            'Personen': data.personenId ? [data.personenId] : undefined,
            'Functie (handmatig)': functie,
            'Calltijd': parseDuration(calltijd),
            'Telefoon (handmatig)': telefoon,
            'Opmerkingen': opmerkingen
          })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        // Verborgen kopieën van dit crewlid (zelfde persoon, geen eigen gegevens) gaan mee weg.
        await verwijderCrewDubbelen(initialData._dubbelen);
      } else {
        // Create new
        console.log('[CrewDialog] Creating crew with:', data);
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-crew`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[CrewDialog] Error response:', errData);
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        const result = await response.json();
        console.log('[CrewDialog] Created:', result);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving crew:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const displayName = selectedPersoon 
    ? (selectedPersoon.fields?.['Volledige naam'] || selectedPersoon.fields?.Naam || persoon) 
    : persoon;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Crew bewerken' : 'Crew toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Teamledenlijst kon niet laden → val terug op handmatig invoeren (nooit lege dialog). */}
          {personen.length === 0 && !initialData && (
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Teamledenlijst is niet geladen — vul de crew hieronder handmatig in (of herlaad de pagina).
            </Typography>
          )}
          {/* Toggle buttons voor selecteren vs handmatig */}
          {personen.length > 0 && !initialData && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button 
                variant={!useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(false)}
                sx={{ flex: 1 }}
              >
                📋 Selecteren
              </Button>
              <Button 
                variant={useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(true)}
                sx={{ flex: 1 }}
              >
                ✏️ Handmatig
              </Button>
            </Box>
          )}

          {/* OPTIE 1: Selecteren uit database */}
          {!useManual && personen.length > 0 && (
            <Autocomplete
              options={personen}
              value={selectedPersoon}
              onChange={handlePersonSelect}
              getOptionLabel={(option) => option.fields?.['Volledige naam'] || option.fields?.Naam || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.fields?.['Volledige naam'] || option.fields?.Naam}
                    </Typography>
                    {option.fields?.Functie && (
                      <Typography variant="caption" color="text.secondary">
                        {Array.isArray(option.fields.Functie) ? option.fields.Functie.join(', ') : option.fields.Functie}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Selecteer teamlid" placeholder="Zoek op naam..." />
              )}
              noOptionsText="Geen teamleden gevonden"
              fullWidth
            />
          )}
          {!useManual && !initialData && (
            <Button size="small" startIcon={<AddIcon />} onClick={createCrewContact} disabled={creatingContact}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              {creatingContact ? 'Aanmaken…' : 'Nieuw contact aanmaken'}
            </Button>
          )}

          {/* OPTIE 2: Handmatig invoeren - extra velden (ook als de lijst leeg/niet geladen is) */}
          {(useManual || initialData || personen.length === 0) && (
            <>
              <Autocomplete
                freeSolo
                options={FUNCTIE_OPTIONS}
                value={functie}
                onChange={(_e, v) => setFunctie(v || '')}
                onInputChange={(_e, v, reason) => { if (reason === 'input') setFunctie(v); }}
                renderInput={(params) => <TextField {...params} label="Functie (kies of typ 'anders')" fullWidth />}
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              
              {/* Naam veld bij handmatige invoer (of als de lijst niet geladen is) */}
              {(useManual || (personen.length === 0 && !initialData)) && (
                <>
                  <Divider />
                  <TextField
                    label="Naam (handmatig)"
                    value={persoon}
                    onChange={(e) => setPersoon(e.target.value)}
                    fullWidth
                    autoFocus={personen.length === 0}
                    helperText="Naam van crew member als deze niet in de database staat"
                  />
                </>
              )}
            </>
          )}

          {/* Extra velden knop alleen bij selecteren */}
          {!useManual && !initialData && selectedPersoon && !showAdvanced && (
            <Button 
              size="small" 
              onClick={() => setShowAdvanced(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              color="secondary"
            >
              + Extra velden (functie, calltijd, etc.)
            </Button>
          )}

          {/* Extra velden bij selecteren */}
          {!useManual && !initialData && showAdvanced && (
            <>
              <Divider sx={{ my: 1 }} />
              <Autocomplete
                freeSolo
                options={FUNCTIE_OPTIONS}
                value={functie}
                onChange={(_e, v) => setFunctie(v || '')}
                onInputChange={(_e, v, reason) => { if (reason === 'input') setFunctie(v); }}
                renderInput={(params) => <TextField {...params} label="Functie (kies of typ 'anders')" fullWidth />}
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || (!selectedPersoon && !persoon)}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Inline vrije-keuze veld voor de planning-tabel: kies uit de bovenaan toegevoegde
// items (locaties/cast) OF typ een eigen waarde. Commit op blur/enter (niet per toets).
function PlanningCombo({ value, options = [], onCommit, placeholder }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const commit = (v) => { const s = (v ?? '').trim(); if (s !== (value || '')) onCommit(s); };
  return (
    <Autocomplete
      freeSolo size="small" options={options}
      value={val} inputValue={val}
      onInputChange={(_e, v) => setVal(v)}
      onChange={(_e, v) => { setVal(v || ''); commit(v || ''); }}
      onBlur={() => commit(val)}
      renderInput={(params) => (
        <TextField {...params} variant="standard" placeholder={placeholder}
          sx={{ fontSize: 13, minWidth: 100, '& .MuiInput-root': { fontSize: 13 } }} />
      )}
      sx={{ minWidth: 110 }}
    />
  );
}

// Multi-select cast voor de planning-tabel: kies meerdere cast-leden (of typ vrij).
// Wordt opgeslagen als komma-gescheiden tekst in het multilineText-veld 'Cast'.
function PlanningCastMulti({ value, options = [], onCommit, placeholder }) {
  const toArr = (v) => String(v || '').split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const [vals, setVals] = useState(toArr(value));
  useEffect(() => { setVals(toArr(value)); }, [value]);
  const commit = (arr) => {
    const joined = arr.join(', ');
    if (joined !== (value || '')) onCommit(joined);
  };
  return (
    <Autocomplete
      freeSolo multiple size="small" options={options} value={vals}
      onChange={(_e, v) => {
        const arr = v.map(s => String(s).trim()).filter(Boolean);
        setVals(arr); commit(arr);
      }}
      ChipProps={{ size: 'small' }}
      renderInput={(params) => (
        <TextField {...params} variant="standard" placeholder={vals.length ? '' : placeholder}
          sx={{ fontSize: 13, minWidth: 120, '& .MuiInput-root': { fontSize: 13 } }} />
      )}
      sx={{ minWidth: 130 }}
    />
  );
}

// ============ PLANNING DIALOG ============
function PlanningDialog({ open, onClose, onSave, onAfterSave, initialData, callsheetId }) {
  const [tijd, setTijd] = useState('');
  const [duur, setDuur] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [locatie, setLocatie] = useState('');
  const [cast, setCast] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setTijd(formatDuration(initialData.fields?.Tijd));
      setDuur(initialData.fields?.['Duur (minuten)'] || '');
      setOmschrijving(initialData.fields?.Omschrijving || '');
      setLocatie(initialData.fields?.Locatie || '');
      setCast(initialData.fields?.Cast || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
    } else {
      setTijd('');
      setDuur('');
      setOmschrijving('');
      setLocatie('');
      setCast('');
      setOpmerkingen('');
    }
  }, [initialData, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (initialData?.id) {
        await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Tijd': parseDuration(tijd),
            'Duur (minuten)': parseInt(duur) || 0,
            'Omschrijving': omschrijving,
            'Locatie': locatie,
            'Cast': cast,
            'Opmerkingen': opmerkingen
          })
        });
      } else {
        await authFetch(`${BACKEND_URL}/Api/callsheet-planning`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            callsheetId,
            tijd: parseDuration(tijd),
            duur: parseInt(duur) || 0,
            omschrijving,
            locatie,
            cast,
            opmerkingen
          })
        });
      }
      if (onAfterSave) {
        await onAfterSave();
      } else if (onSave) {
        onSave();
      }
      onClose();
    } catch (err) {
      console.error('Error saving planning:', err);
      toast.error('Fout bij opslaan planning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Planning bewerken' : 'Planning toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Tijd (HH:MM)"
                value={tijd}
                onChange={(e) => setTijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Duur (minuten)"
                value={duur}
                onChange={(e) => setDuur(e.target.value)}
                type="number"
                fullWidth
              />
            </Grid>
          </Grid>
          <TextField
            label="Omschrijving"
            value={omschrijving}
            onChange={(e) => setOmschrijving(e.target.value)}
            fullWidth
          />
          <TextField
            label="Locatie"
            value={locatie}
            onChange={(e) => setLocatie(e.target.value)}
            fullWidth
          />
          <TextField
            label="Cast"
            value={cast}
            onChange={(e) => setCast(e.target.value)}
            fullWidth
          />
          <TextField
            label="Opmerkingen"
            value={opmerkingen}
            onChange={(e) => setOpmerkingen(e.target.value)}
            multiline
            rows={2}
            fullWidth
            inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============ CAST DIALOG ============
function CastDialog({ open, onClose, onSave, initialData, callsheetId, castingOptions = [] }) {
  const [selectedCast, setSelectedCast] = useState(null);
  const [naam, setNaam] = useState('');
  const [rol, setRol] = useState('');
  const [calltijd, setCalltijd] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [saving, setSaving] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);

  // Maak een nieuw, herbruikbaar Casting-record aan en koppel het meteen.
  const createCastContact = async () => {
    const nm = (window.prompt('Naam van het nieuwe cast-contact:', naam || '') || '').trim();
    if (!nm) return;
    setCreatingContact(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-new-cast-contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ naam: nm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSelectedCast({ id: data.id, fields: { Naam: data.naam } });
      setNaam(data.naam);
      toast.success(`Contact "${data.naam}" aangemaakt`);
    } catch (err) {
      toast.error(`Aanmaken mislukt: ${err.message}`);
    } finally {
      setCreatingContact(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      setNaam(initialData.fields?.['Cast (handmatig)'] || '');
      setRol(initialData.fields?.Rol || '');
      setCalltijd(formatDuration(initialData.fields?.Calltijd));
      setTelefoon(initialData.fields?.['Telefoon (handmatig)'] || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
      // Try to find the linked cast
      const linkedCastId = initialData.fields?.Cast?.[0];
      if (linkedCastId && castingOptions.length > 0) {
        const found = castingOptions.find(c => c.id === linkedCastId);
        setSelectedCast(found || null);
        setUseManual(!found);
      } else {
        setSelectedCast(null);
        setUseManual(true);
      }
      setShowAdvanced(true); // Show advanced fields when editing
    } else {
      setSelectedCast(null);
      setNaam('');
      setRol('');
      setCalltijd('');
      setTelefoon('');
      setOpmerkingen('');
      setUseManual(false);
      setShowAdvanced(false); // Hide advanced fields for new entries
    }
  }, [initialData, open, castingOptions]);

  const handleCastSelect = (event, newValue) => {
    setSelectedCast(newValue);
    if (newValue) {
      setNaam(newValue.fields?.Naam || '');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (initialData?.id) {
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-cast/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Cast': selectedCast ? [selectedCast.id] : undefined,
            'Cast (handmatig)': naam,
            'Rol': rol,
            'Calltijd': parseDuration(calltijd),
            'Telefoon (handmatig)': telefoon,
            'Opmerkingen': opmerkingen
          })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
      } else {
        const payload = {
          callsheetId,
          castId: selectedCast?.id,
          naam: naam || (selectedCast?.fields?.Naam || ''),
          rol,
          calltijd: parseDuration(calltijd),
          telefoon,
          opmerkingen
        };
        console.log('[CastDialog] Creating cast with:', payload);
        
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-cast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[CastDialog] Error response:', errData);
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        
        const result = await response.json();
        console.log('[CastDialog] Created:', result);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving cast:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Cast bewerken' : 'Cast toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Toggle buttons voor selecteren vs handmatig */}
          {castingOptions.length > 0 && !initialData && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button 
                variant={!useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(false)}
                sx={{ flex: 1 }}
              >
                📋 Selecteren
              </Button>
              <Button 
                variant={useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(true)}
                sx={{ flex: 1 }}
              >
                ✏️ Handmatig
              </Button>
            </Box>
          )}

          {/* OPTIE 1: Selecteren uit database */}
          {!useManual && castingOptions.length > 0 && (
            <Autocomplete
              options={castingOptions}
              value={selectedCast}
              onChange={handleCastSelect}
              getOptionLabel={(option) => option.fields?.Naam || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body2" fontWeight={500}>
                    {option.fields?.Naam}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Selecteer cast" placeholder="Zoek op naam..." />
              )}
              noOptionsText="Geen cast gevonden"
              fullWidth
            />
          )}
          {!useManual && !initialData && (
            <Button size="small" startIcon={<AddIcon />} onClick={createCastContact} disabled={creatingContact}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              {creatingContact ? 'Aanmaken…' : 'Nieuw contact aanmaken'}
            </Button>
          )}

          {/* OPTIE 2: Handmatig invoeren - extra velden */}
          {(useManual || initialData) && (
            <>
              {/* Naam veld bovenaan bij handmatige invoer */}
              {useManual && (
                <TextField
                  label="Naam"
                  value={naam}
                  onChange={(e) => setNaam(e.target.value)}
                  fullWidth
                  autoFocus
                  placeholder="Naam van cast member"
                  helperText="Naam als deze niet in de database staat"
                />
              )}
              <TextField
                label="Rol"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                fullWidth
                placeholder="bijv. Hoofdpersoon, Figurant..."
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
            </>
          )}

          {/* Extra velden knop alleen bij selecteren */}
          {!useManual && !initialData && selectedCast && !showAdvanced && (
            <Button 
              size="small" 
              onClick={() => setShowAdvanced(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              color="secondary"
            >
              + Extra velden (rol, calltijd, etc.)
            </Button>
          )}

          {/* Extra velden bij selecteren */}
          {!useManual && !initialData && showAdvanced && (
            <>
              <Divider sx={{ my: 1 }} />
              <TextField
                label="Rol"
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                fullWidth
                placeholder="bijv. Hoofdpersoon, Figurant..."
              />
              <TextField
                label="Calltijd (HH:MM)"
                value={calltijd}
                onChange={(e) => setCalltijd(e.target.value)}
                placeholder="09:00"
                fullWidth
              />
              <TextField
                label="Telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || (!selectedCast && !naam)}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============ LOCATIE DIALOG ============
// Adres-autocomplete via PDOK Locatieserver (gratis, geen API-key, NL-adressen, CORS)
// + bedrijfsnaam/POI-zoek via OSM Nominatim (zo vind je "Alliander" / "Enexis" op naam).
// onSelect krijgt {straat, postcode, plaats, naam?}. Vult de locatievelden.
const PDOK_BASE = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1';
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
function PdokAdresZoeker({ onSelect, eerderGebruikt = [] }) {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const debounceRef = useRef(null);
  const fetchSuggest = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 3) { setOpts([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        // PDOK (adressen) + Nominatim (bedrijfsnamen/POI's) parallel; merge tot één lijst.
        // bewust stil: externe suggest-bronnen, als één bron uitvalt tonen we de andere gewoon.
        const [pdok, osm] = await Promise.all([
          fetch(`${PDOK_BASE}/suggest?q=${encodeURIComponent(q)}&fq=type:adres&rows=6`)
            .then(r => r.json()).catch(() => null),
          fetch(`${NOMINATIM_BASE}/search?q=${encodeURIComponent(q)}&format=jsonv2&countrycodes=nl&addressdetails=1&limit=5`)
            .then(r => r.json()).catch(() => null),
        ]);
        if (seq !== seqRef.current) return;
        const pdokOpts = (pdok?.response?.docs || []).map(d => ({
          id: `pdok:${d.id}`, source: 'pdok', pdokId: d.id, label: d.weergavenaam,
        }));
        const osmOpts = (Array.isArray(osm) ? osm : []).map((d, i) => {
          const a = d.address || {};
          return {
            id: `osm:${d.place_id || i}`, source: 'osm',
            label: `🏢 ${d.display_name}`,
            naam: a.amenity || a.office || a.building || a.shop || (d.name) || (d.display_name || '').split(',')[0],
            straat: [a.road, a.house_number].filter(Boolean).join(' ').trim(),
            postcode: (a.postcode || '').toUpperCase(),
            plaats: a.city || a.town || a.village || a.municipality || '',
          };
        });
        // Eerder gebruikte locaties (eigen bibliotheek) altijd bovenaan: "Huis Tonneke" moet
        // de Tilburgse van vorige keer geven, niet een gelijknamige POI elders (Joris, 7 sep).
        const zoek = q.toLowerCase();
        const eigen = (eerderGebruikt || []).filter((l) => {
          const f = l.fields || {};
          return [f['Naam locatie'], f.Straat, f.Plaats, f.Adres].some((v) => String(v || '').toLowerCase().includes(zoek));
        }).slice(0, 5).map((l) => {
          const f = l.fields || {};
          return {
            id: `eigen:${l.id}`, source: 'eigen',
            label: `⭐ ${f['Naam locatie'] || 'Locatie'} — ${[f.Straat, f.Plaats].filter(Boolean).join(', ')} (eerder gebruikt)`,
            naam: f['Naam locatie'] || '', straat: f.Straat || '', postcode: f.Postcode || '', plaats: f.Plaats || '',
            parkeren: f.Parkeren || '', contactpersoon: f.Contactpersoon || '', telefoon: f.Telefoonnummer || '',
          };
        });
        setOpts([...eigen, ...osmOpts, ...pdokOpts]);
      } catch { /* stil */ } finally { if (seq === seqRef.current) setLoading(false); }
    }, 300);
  };
  const lookupPdok = async (pdokId) => {
    try {
      const res = await fetch(`${PDOK_BASE}/lookup?id=${encodeURIComponent(pdokId)}&fl=straatnaam,huis_nlt,postcode,woonplaatsnaam`);
      const doc = (await leesJson(res, 'Adres opzoeken (PDOK)'))?.response?.docs?.[0];
      if (!doc) return;
      const pc = (doc.postcode || '').replace(/(\d{4})\s?([A-Za-z]{2})/, '$1 $2').toUpperCase();
      onSelect({
        straat: [doc.straatnaam, doc.huis_nlt].filter(Boolean).join(' ').trim(),
        postcode: pc,
        plaats: doc.woonplaatsnaam || '',
      });
    } catch (e) { meldFout(e, 'Adres opzoeken (PDOK)'); }
  };
  const handleSelect = (val) => {
    if (!val) return;
    if (val.source === 'pdok') { lookupPdok(val.pdokId); return; }
    // OSM/eigen: adresdetails zitten al in de optie.
    const pc = (val.postcode || '').replace(/(\d{4})\s?([A-Za-z]{2})/, '$1 $2').toUpperCase();
    onSelect({ straat: val.straat || '', postcode: pc, plaats: val.plaats || '', naam: val.naam || '',
      parkeren: val.parkeren || '', contactpersoon: val.contactpersoon || '', telefoon: val.telefoon || '', eigen: val.source === 'eigen' });
  };
  return (
    <Autocomplete
      options={opts}
      loading={loading}
      filterOptions={(x) => x}   // server-side filtering (PDOK + Nominatim)
      getOptionLabel={(o) => o?.label || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onInputChange={(_e, val, reason) => { if (reason === 'input') fetchSuggest(val); }}
      onChange={(_e, val) => handleSelect(val)}
      noOptionsText="Typ minstens 3 tekens…"
      renderInput={(params) => <TextField {...params} size="small" label="Adres of bedrijf zoeken"
        placeholder="Straat huisnummer, of bedrijfsnaam (bv. Alliander)…" />}
      sx={{ mb: 1 }}
    />
  );
}

// Picker voor klant-contactpersoon uit de Bedrijven-tabel. onSelect krijgt {naam, telefoon}.
function KlantContactPicker({ onSelect, currentLabel = '' }) {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);          // alleen het resultaat van de LAATSTE query tonen
  const debounceRef = useRef(null);
  const fetchOpts = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/callsheet-klant-contacten?q=${encodeURIComponent(q || '')}`);
        const data = await leesJson(res, 'Klantcontacten laden');
        if (seq === seqRef.current) setOpts(data.contacten || []);  // negeer verouderde antwoorden
      } catch (e) { if (seq === seqRef.current) { meldFout(e, 'Klantcontacten laden'); setOpts([]); } } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 250);
  };
  return (
    <Autocomplete
      options={opts}
      loading={loading}
      filterOptions={(x) => x}   // server-side zoeken (op naam ÉN bedrijf) — geen client-filter
      onInputChange={(_e, val, reason) => { if (reason === 'input') fetchOpts(val); }}
      getOptionLabel={(o) => o?.naam || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, val) => { if (val) onSelect(val); }}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id}>
          <Box>
            <Typography variant="body2" fontWeight={500}>{o.naam}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[o.bedrijf, telefoonNl(o.telefoon)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} size="small"
        label="Contactpersoon zoeken (Bedrijven + intern)"
        placeholder={currentLabel ? `Huidig: ${currentLabel}` : 'Zoek op naam of bedrijf…'}
        helperText={currentLabel ? `Huidige contactpersoon: ${currentLabel}` : ''} />}
      noOptionsText="Geen contacten gevonden"
      sx={{ mb: 2 }}
    />
  );
}

// Kiest de Full Frame-persoon die de settelefoon draagt. Schrijft het gekoppelde
// veld 'Contactpersoon Settelefoon' (link naar Personen); het nummer wordt door
// Airtable opgezocht (FullFrame Settelefoonnummer). Alleen koppelbare hoofd-base
// personen (geen Leveranciers-base _source).
function SettelefoonPicker({ personen, currentLabel, onSelect }) {
  const naamVan = (p) => p?.fields?.['Volledige naam'] || p?.fields?.Naam || '';
  const telVan = (p) => {
    const t = p?.fields?.Telefoonnummer;
    return Array.isArray(t) ? t[0] : (t || '');
  };
  const options = (personen || []).filter(p => !p._source);
  return (
    <Autocomplete
      options={options}
      getOptionLabel={naamVan}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, val) => { if (val) onSelect(val); }}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id}>
          <Box>
            <Typography variant="body2" fontWeight={500}>{naamVan(o)}</Typography>
            {telVan(o) && <Typography variant="caption" color="text.secondary">{telefoonNl(telVan(o))}</Typography>}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} size="small" label="Settelefoon (Full Frame contactpersoon)"
          placeholder={currentLabel ? `Huidig: ${currentLabel}` : 'Kies wie de settelefoon draagt…'} />
      )}
      noOptionsText="Geen personen"
      sx={{ mb: 2 }}
    />
  );
}

// Parse een geplakt adresblok naar losse velden. Werkt op regels (of komma's bij één regel).
// NL-postcode `1234 AB`, URL, straat (regel met huisnummer), naam (eerste regel).
function parsePastedLocation(text) {
  let lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length <= 1 && text.includes(',')) lines = text.split(',').map(l => l.trim()).filter(Boolean);
  const out = { naam: '', straat: '', postcode: '', plaats: '', url: '' };
  const used = new Set();
  const pcRe = /\b(\d{4})\s?([A-Za-z]{2})\b/;
  const urlRe = /(https?:\/\/\S+|www\.\S+)/i;
  // URL
  lines.forEach((l, i) => { const m = l.match(urlRe); if (m && !out.url) { out.url = m[0]; used.add(i); } });
  // Postcode + plaats
  lines.forEach((l, i) => {
    if (used.has(i) || out.postcode) return;
    const m = l.match(pcRe);
    if (m) {
      out.postcode = `${m[1]} ${m[2].toUpperCase()}`;
      const rest = l.replace(m[0], '').replace(/^[,\s-]+|[,\s-]+$/g, '').trim();
      if (rest) out.plaats = rest;
      used.add(i);
    }
  });
  // Naam = eerste ongebruikte regel
  for (let i = 0; i < lines.length; i++) { if (!used.has(i)) { out.naam = lines[i]; used.add(i); break; } }
  // Straat = eerste resterende regel met een cijfer (huisnummer)
  for (let i = 0; i < lines.length; i++) { if (!used.has(i) && /\d/.test(lines[i])) { out.straat = lines[i]; used.add(i); break; } }
  return out;
}

function LocatieDialog({ open, onClose, onSave, initialData, callsheetId, existingLocaties = [] }) {
  const [pasteText, setPasteText] = useState('');
  const [selectedLocatie, setSelectedLocatie] = useState(null);
  const [naam, setNaam] = useState('');
  const [straat, setStraat] = useState('');
  const [postcode, setPostcode] = useState('');
  const [plaats, setPlaats] = useState('');
  const [contactpersoon, setContactpersoon] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [parkeren, setParkeren] = useState('');
  const [parkeerfoto, setParkeerfoto] = useState('');   // multiline: 1 (signed) blob-URL per regel
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [parkSuggest, setParkSuggest] = useState(null);  // centrale parkeer-lookup voorstel
  const [saving, setSaving] = useState(false);
  const [useManual, setUseManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fotoUrls = parkeerfoto.split('\n').map(s => s.trim()).filter(Boolean);
  const handleFotoUpload = async (file) => {
    if (!file) return;
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-parking-image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setParkeerfoto(prev => [...prev.split('\n').map(s => s.trim()).filter(Boolean), data.url].join('\n'));
    } catch (err) {
      toast.error(`Upload mislukt: ${err.message}`);
    } finally {
      setUploadingFoto(false);
    }
  };
  const removeFoto = (url) => setParkeerfoto(prev => prev.split('\n').map(s => s.trim()).filter(Boolean).filter(u => u !== url).join('\n'));

  // Centrale "parkeer-database per adres": zoek of dit adres al eerder parkeerinfo had.
  const lookupParking = async (s, pc, pl) => {
    if (!pc && !s) return;
    try {
      const qs = new URLSearchParams({ straat: s || '', postcode: pc || '', plaats: pl || '' });
      const res = await authFetch(`${BACKEND_URL}/Api/callsheet-parking-lookup?${qs}`);
      const data = await leesJson(res, 'Parkeerinfo opzoeken');
      if (data?.match && (data.match.parkeren || data.match.parkeerfoto)) setParkSuggest(data.match);
    } catch (e) { console.warn('[LocatieDialog] parkeer-lookup:', e.message); /* bewust stil: optionele suggestie, geen blokkade voor het formulier */ }
  };

  useEffect(() => {
    if (initialData) {
      setNaam(initialData.fields?.['Naam locatie'] || '');
      setStraat(initialData.fields?.Straat || '');
      setPostcode(initialData.fields?.Postcode || '');
      setPlaats(initialData.fields?.Plaats || '');
      setContactpersoon(initialData.fields?.Contactpersoon || '');
      setTelefoon(initialData.fields?.Telefoonnummer || '');
      setOpmerkingen(initialData.fields?.Opmerkingen || '');
      setParkeren(initialData.fields?.Parkeren || '');
      setParkeerfoto(initialData.fields?.Parkeerfoto || '');
      setUseManual(true); // When editing, show manual fields
      setShowAdvanced(true); // Show all fields when editing
    } else {
      setSelectedLocatie(null);
      setNaam('');
      setStraat('');
      setPostcode('');
      setPlaats('');
      setContactpersoon('');
      setTelefoon('');
      setOpmerkingen('');
      setParkeren('');
      setParkeerfoto('');
      setUseManual(existingLocaties.length === 0); // If no existing locations, show manual
      setShowAdvanced(false); // Hide advanced fields for new entries
    }
    setParkSuggest(null);
  }, [initialData, open, existingLocaties.length]);

  const handleLocatieSelect = (event, newValue) => {
    setSelectedLocatie(newValue);
    if (newValue) {
      // Pre-fill fields from selected location
      setNaam(newValue.fields?.['Naam locatie'] || '');
      setStraat(newValue.fields?.Straat || '');
      setPostcode(newValue.fields?.Postcode || '');
      setPlaats(newValue.fields?.Plaats || '');
      setContactpersoon(newValue.fields?.Contactpersoon || '');
      setTelefoon(newValue.fields?.Telefoonnummer || '');
      setParkeren(newValue.fields?.Parkeren || '');
      setParkeerfoto(newValue.fields?.Parkeerfoto || '');
      // Don't copy opmerkingen - let user add their own
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (initialData?.id) {
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-locaties/${initialData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'Naam locatie': naam,
            'Straat': straat,
            'Postcode': postcode,
            'Plaats': plaats,
            'Contactpersoon': contactpersoon,
            'Telefoonnummer': telefoon,
            'Opmerkingen': opmerkingen,
            'Parkeren': parkeren,
            'Parkeerfoto': parkeerfoto
          })
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
      } else {
        const payload = {
          callsheetId,
          naam,
          straat,
          postcode,
          plaats,
          contactpersoon,
          telefoon,
          opmerkingen,
          parkeren,
          parkeerfoto
        };
        console.log('[LocatieDialog] Creating location with:', payload);
        const response = await authFetch(`${BACKEND_URL}/Api/callsheet-locaties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[LocatieDialog] Error response:', errData);
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
        const result = await response.json();
        console.log('[LocatieDialog] Created:', result);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving locatie:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Gedeeld blok: parkeerfoto's uploaden + tonen (gebruikt in beide invoer-paden).
  const parkeerfotoBlock = (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button component="label" size="small" variant="outlined" startIcon={<PhotoCameraIcon fontSize="small" />} disabled={uploadingFoto}>
          {uploadingFoto ? 'Uploaden…' : 'Parkeerfoto toevoegen'}
          <input hidden type="file" accept="image/*" capture="environment"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFotoUpload(f); }} />
        </Button>
        {uploadingFoto && <CircularProgress size={16} />}
      </Box>
      {fotoUrls.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {fotoUrls.map((url) => (
            <Box key={url} sx={{ position: 'relative', width: 84, height: 64 }}>
              <Box component="img" src={url} alt="parkeerfoto"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb' }} />
              <IconButton size="small" onClick={() => removeFoto(url)}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', boxShadow: 1, p: 0.25, '&:hover': { bgcolor: '#fee2e2' } }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Locatie bewerken' : 'Locatie toevoegen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Toggle buttons voor selecteren vs handmatig */}
          {existingLocaties.length > 0 && !initialData && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button 
                variant={!useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(false)}
                sx={{ flex: 1 }}
              >
                📋 Selecteren
              </Button>
              <Button 
                variant={useManual ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setUseManual(true)}
                sx={{ flex: 1 }}
              >
                ✏️ Handmatig
              </Button>
            </Box>
          )}

          {/* OPTIE 1: Selecteren uit database */}
          {!useManual && existingLocaties.length > 0 && !initialData && (
            <Autocomplete
              options={existingLocaties}
              value={selectedLocatie}
              onChange={handleLocatieSelect}
              getOptionLabel={(option) => {
                const naam = option.fields?.['Naam locatie'] || '';
                const plaats = option.fields?.Plaats || '';
                return plaats ? `${naam} (${plaats})` : naam;
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.fields?.['Naam locatie']}
                    </Typography>
                    {(option.fields?.Straat || option.fields?.Plaats) && (
                      <Typography variant="caption" color="text.secondary">
                        {[option.fields?.Straat, option.fields?.Postcode, option.fields?.Plaats].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Selecteer bestaande locatie" placeholder="Zoek op naam of plaats..." />
              )}
              noOptionsText="Geen locaties gevonden"
              fullWidth
            />
          )}

          {/* OPTIE 2: Handmatig invoeren of bewerken */}
          {(useManual || initialData) && (
            <>
              {/* Adres zoeken via PDOK (gratis) → vult straat/postcode/plaats. */}
              <PdokAdresZoeker
                eerderGebruikt={existingLocaties}
                onSelect={({ straat: s, postcode: pc, plaats: pl, naam: nm, parkeren: pk, eigen }) => {
                  if (s) setStraat(s);
                  if (pc) setPostcode(pc);
                  if (pl) setPlaats(pl);
                  if (nm && (!naam || eigen)) setNaam(nm);   // bedrijfsnaam invullen als nog leeg (eigen locatie: altijd)
                  if (pk && eigen && !parkeren) setParkeren(pk);
                  if (!parkeren && !pk) lookupParking(s, pc, pl);  // bekend parkeeradvies voorstellen
                }}
              />
              {parkSuggest && !parkeren && (
                <Alert severity="info" sx={{ py: 0.5 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => {
                      setParkeren(parkSuggest.parkeren || '');
                      if (parkSuggest.parkeerfoto) setParkeerfoto(parkSuggest.parkeerfoto);
                      setParkSuggest(null);
                    }}>
                      Overnemen
                    </Button>
                  }>
                  Parkeerinfo bekend voor dit adres{parkSuggest.naam ? ` (${parkSuggest.naam})` : ''}:
                  <Box component="span" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
                    {(parkSuggest.parkeren || '').slice(0, 120)}{(parkSuggest.parkeren || '').length > 120 ? '…' : ''}
                  </Box>
                </Alert>
              )}
              {/* Snel invullen: plak een adresblok (naam / straat / postcode plaats / url). */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label="Plak adres (naam, straat, postcode plaats, url)"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  placeholder={'Hercuton\nTouwslager 2\n5253 RK Nieuwkuijk\nhttps://www.hercuton.nl/'}
                />
                <Button
                  variant="outlined"
                  disabled={!pasteText.trim()}
                  onClick={() => {
                    const p = parsePastedLocation(pasteText);
                    if (p.naam) setNaam(p.naam);
                    if (p.straat) setStraat(p.straat);
                    if (p.postcode) setPostcode(p.postcode);
                    if (p.plaats) setPlaats(p.plaats);
                    if (p.url) setOpmerkingen((prev) => prev ? `${prev}\n${p.url}` : p.url);
                    setPasteText('');
                  }}
                  sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                >
                  Invullen
                </Button>
              </Box>
              <TextField
                label="Straat + huisnummer"
                value={straat}
                onChange={(e) => setStraat(e.target.value)}
                fullWidth
              />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    onBlur={() => { if (!parkeren) lookupParking(straat, postcode, plaats); }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    label="Plaats"
                    value={plaats}
                    onChange={(e) => setPlaats(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <TextField
                label="Contactpersoon"
                value={contactpersoon}
                onChange={(e) => setContactpersoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefoonnummer"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              <TextField
                label="Parkeerinstructies"
                value={parkeren}
                onChange={(e) => setParkeren(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="bijv. parkeren op P2, aanmelden bij receptie, of plak een parkeeradres"
                helperText="Verschijnt op de PDF onder de locatie"
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              {parkeerfotoBlock}

              {/* Naam veld onderaan bij handmatige invoer */}
              <Divider />
              <TextField
                label="Naam locatie"
                value={naam}
                onChange={(e) => setNaam(e.target.value)}
                fullWidth
                helperText="Geef een herkenbare naam aan deze locatie"
              />
            </>
          )}

          {/* Extra velden knop alleen bij selecteren */}
          {!useManual && !initialData && selectedLocatie && !showAdvanced && (
            <Button 
              size="small" 
              onClick={() => setShowAdvanced(true)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              color="secondary"
            >
              + Extra velden (adres, contactpersoon, etc.)
            </Button>
          )}

          {/* Extra velden bij selecteren */}
          {!useManual && !initialData && showAdvanced && selectedLocatie && (
            <>
              <Divider sx={{ my: 1 }} />
              <TextField
                label="Straat + huisnummer"
                value={straat}
                onChange={(e) => setStraat(e.target.value)}
                fullWidth
              />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    onBlur={() => { if (!parkeren) lookupParking(straat, postcode, plaats); }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    label="Plaats"
                    value={plaats}
                    onChange={(e) => setPlaats(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <TextField
                label="Contactpersoon"
                value={contactpersoon}
                onChange={(e) => setContactpersoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefoonnummer"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                fullWidth
              />
              <TextField
                label="Opmerkingen"
                value={opmerkingen}
                onChange={(e) => setOpmerkingen(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              <TextField
                label="Parkeerinstructies"
                value={parkeren}
                onChange={(e) => setParkeren(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="bijv. parkeren op P2, aanmelden bij receptie, of plak een parkeeradres"
                helperText="Verschijnt op de PDF onder de locatie"
                inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
              />
              {parkeerfotoBlock}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !naam}>
          {saving ? <CircularProgress size={20} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Bestandsnaam voor de callsheet-PDF — moet matchen met de backend
// (backend/routes/callsheets.js): "Callsheet - <Titel> - <YYYY-MM-DD>.pdf".
// Nodig omdat een blob-download met a.download het server-Content-Disposition negeert.
function buildPdfFilename(fields = {}) {
  const safeTitle = String(fields.Titel || 'Callsheet')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 80) || 'Naamloos';
  const datumRaw = fields['Datum (handmatig)'] || fields.Datum;
  const datumStr = datumRaw ? String(datumRaw).slice(0, 10) : '';
  return `Callsheet - ${safeTitle}${datumStr ? ` - ${datumStr}` : ''}.pdf`;
}


// Module-level zodat de component-identiteit stabiel blijft tussen renders.
// (Eerder stond dit binnen CallSheetManager → elke setEditValue maakte een nieuwe
//  functie-identiteit → React unmount/remount van de TextField → cursor sprong naar
//  positie 0 → elk getypt teken kwam vooraan = "spiegelbeeld"-tekst. Zie #algemene-opmerkingen.)
function EditableField({ label, fieldName, value, type = 'text', multiline = false, dark = false, ctx }) {
  const { editingField, editValue, setEditValue, updateField, setEditingField } = ctx;
  const isEditing = editingField === fieldName;
  // Voorkomt dat het annuleren-kruisje (of Escape) via de blur tóch opslaat.
  const skipBlurRef = useRef(false);

  if (isEditing) {
    // Auto-save: bij wegklikken (blur) en bij Enter (enkele regel). Escape annuleert.
    const commit = () => updateField(fieldName, editValue);
    const cancel = () => { skipBlurRef.current = true; setEditingField(null); };
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => {
            if (skipBlurRef.current) { skipBlurRef.current = false; return; }
            commit();   // automatisch opslaan zodra je het veld verlaat
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !multiline && !e.shiftKey) {
              e.preventDefault();
              skipBlurRef.current = true;  // blur na sluiten niet dubbel laten opslaan
              commit();
            } else if (e.key === 'Escape') {
              cancel();
            }
          }}
          size="small"
          fullWidth
          // Tekstvelden groeien mee met de inhoud (geen interne scroll) en
          // wrappen lange regels; Enter-gedrag blijft per veldsoort behouden.
          multiline={multiline || type === 'text'}
          minRows={multiline ? 3 : 1}
          type={multiline || type === 'text' ? undefined : type}
          autoFocus
          helperText={multiline ? 'Opgeslagen zodra je hier wegklikt' : 'Opgeslagen bij Enter of wegklikken'}
        />
        {/* Opslaan is nu automatisch; deze knop blijft als bevestiging. */}
        <IconButton size="small" onMouseDown={() => { skipBlurRef.current = true; }} onClick={commit} color="primary">
          <SaveIcon />
        </IconButton>
        <IconButton size="small" onMouseDown={() => { skipBlurRef.current = true; }} onClick={() => setEditingField(null)}>
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      {!dark && <Typography variant="caption" color="text.secondary">{label}</Typography>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant={dark ? 'h5' : 'body2'} sx={{ whiteSpace: 'pre-wrap', ...(dark ? { fontWeight: 800, color: '#1f2937' } : {}) }}>{value || '-'}</Typography>
        <IconButton
          size="small"
          onClick={() => { setEditingField(fieldName); setEditValue(value || ''); }}
          sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

// ============ MAIN CALLSHEET MANAGER ============
/** Slotjes-cache van deze browser (de server is de bron, zie callsheet_extra.vaste_planning). */
function leesLokaleSloten(callsheetId) {
  try { return new Set(JSON.parse(localStorage.getItem(`callsheet-locked-${callsheetId}`) || '[]')); }
  catch { return new Set(); }
}

function CallSheetManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fotoApi = useCallsheetFotos(id);   // locatiefoto's (privé blob, SAS-links)
  useEffect(() => registreerOngedaanSneltoets(), []);   // Ctrl+Z = laatste foto-actie terug

  const skipMissingWarnRef = useRef(false);   // negeer ontbrekende-info melding na 1× bevestigen
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [error, setError] = useState(null);
  const [callsheetData, setCallsheetData] = useState(null);
  const [pdfStatus, setPdfStatus] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', filename: '' }); // in-app PDF-preview
  const [personen, setPersonen] = useState([]);
  const [castingOptions, setCastingOptions] = useState([]);
  const [parking, setParking] = useState({});           // locId → parkeer-resultaat
  const [parkingLoading, setParkingLoading] = useState({});
  const [existingLocaties, setExistingLocaties] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [copyPlanningDialog, setCopyPlanningDialog] = useState(false);
  const [copyPlanningOptions, setCopyPlanningOptions] = useState([]);
  const [copyPlanningSearch, setCopyPlanningSearch] = useState('');
  const [loadingCopyOptions, setLoadingCopyOptions] = useState(false);
  const [clearExistingPlanning, setClearExistingPlanning] = useState(true); // Default: clear existing
  const [copyIncludeCrew, setCopyIncludeCrew] = useState(false);   // crew meekopiëren van bron-callsheet
  const [copyIncludeCast, setCopyIncludeCast] = useState(false);   // cast meekopiëren van bron-callsheet
  const [weather, setWeather] = useState('');

  // 🔒 Vastgezette begintijden per planning-regel — blijven staan bij herberekenen; wat erna
  // komt sluit erop aan (parallel plannen, vaste lunch). Sinds 23 sep op de server
  // (callsheet_extra.vaste_planning): in localStorage zag een collega of ander apparaat ze niet,
  // en de herberekening na opslaan/verwijderen gebruikte een verouderde kopie van de slotjes —
  // Naomi's planning voor de UMC-draaidag sprong daardoor helemaal door.
  const [lockedRows, setLockedRows] = useState(() => leesLokaleSloten(id));
  // Altijd de actuele set: herberekenen gebeurt ook vanuit callbacks die bij het openen zijn gemaakt.
  const lockedRef = useRef(lockedRows);
  useEffect(() => { lockedRef.current = lockedRows; }, [lockedRows]);
  // Welk callsheet nu open is: een laat antwoord voor een vorig callsheet mag de slotjes van
  // het huidige niet overschrijven (Codex-hercheck, 23 sep).
  const huidigIdRef = useRef(id);
  useEffect(() => { huidigIdRef.current = id; }, [id]);
  useEffect(() => {
    const lokaal = leesLokaleSloten(id);
    lockedRef.current = lokaal;
    setLockedRows(lokaal);
  }, [id]);
  const zetSloten = useCallback((lijst) => {
    const set = new Set(lijst || []);
    lockedRef.current = set;
    setLockedRows(set);
    try { localStorage.setItem(`callsheet-locked-${id}`, JSON.stringify([...set])); } catch { /* cache optioneel */ }
  }, [id]);
  // Per klik alleen die ene regel naar de server (vastZetten/vrijGeven), en klikken netjes achter
  // elkaar: zo verliest een snelle tweede klik — of een collega — de eerste niet (Codex-review 23 sep).
  const slotWachtrij = useRef({ keten: Promise.resolve(), open: 0 });
  const bewaarSloten = useCallback(async (wijziging) => {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(wijziging),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
    return d;
  }, [id]);
  const toggleLock = (rowId) => {
    const cid = id;
    const vast = !lockedRef.current.has(rowId);
    const next = new Set(lockedRef.current);
    if (vast) next.add(rowId); else next.delete(rowId);
    lockedRef.current = next;
    setLockedRows(next);
    const w = slotWachtrij.current;
    w.open += 1;
    w.keten = w.keten
      .then(() => bewaarSloten(vast ? { vastZetten: [rowId] } : { vrijGeven: [rowId] }))
      .then((d) => {
        if (huidigIdRef.current === cid && w.open === 1 && Array.isArray(d.vastePlanning)) zetSloten(d.vastePlanning);
      })
      .catch(async (e) => {
        if (huidigIdRef.current !== cid) return;
        setError(`Slotje opslaan mislukt: ${e.message}`);
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${cid}/extra`);
          const d = await r.json();
          if (huidigIdRef.current === cid && Array.isArray(d.vastePlanning)) zetSloten(d.vastePlanning);
        } catch { /* laat de lokale stand staan */ }
      })
      .finally(() => { w.open -= 1; });
  };
  
  // Edit states
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Dialog states
  const [crewDialog, setCrewDialog] = useState({ open: false, data: null });
  const [castDialog, setCastDialog] = useState({ open: false, data: null });
  const [draggedRow, setDraggedRow] = useState(null); // { key:'crew'|'cast', id }
  const [planningDialog, setPlanningDialog] = useState({ open: false, data: null });
  const [locatieDialog, setLocatieDialog] = useState({ open: false, data: null });
  // Levend callsheet (Joris, 15 sep): eerst locatie + begin-/eindtijd. Tijden staan in
  // Postgres (callsheet_extra) omdat Airtable die velden niet heeft.
  const [extra, setExtra] = useState({ begintijd: '', eindtijd: '', tijdenBron: null, briefing: '', briefingBronnen: [] });
  // AI-briefing (Joris 18 sep): voorstel uit notities/meetings/mails/dossier → aanvinken → overnemen.
  const [aiBriefing, setAiBriefing] = useState(null);       // { velden, vragen, bronnen, huidig }
  const [aiKeuze, setAiKeuze] = useState({});                // key → { aan, tekst }
  const [briefingTekst, setBriefingTekst] = useState('');
  const [briefingBezig, setBriefingBezig] = useState(false);
  const [briefingBewerk, setBriefingBewerk] = useState(false);
  const [aiTab, setAiTab] = useState(0);                     // 0 = callsheet (productie), 1 = briefing crew
  const [aiAanwijzingen, setAiAanwijzingen] = useState('');
  const [aiExtraBron, setAiExtraBron] = useState('');
  const [aiConflictKeuze, setAiConflictKeuze] = useState({}); // conflict-index → gekozen waarde
  const [aiConflictStart, setAiConflictStart] = useState({}); // de standaardkeuze, om 'gewijzigd' te tonen
  const [aiBronnenOpen, setAiBronnenOpen] = useState(false);
  const [aiBriefingBewerk, setAiBriefingBewerk] = useState(false);   // briefing crew in de dialoog: opgemaakt (links) of ruwe tekst
  const [aiExtraKeuze, setAiExtraKeuze] = useState({});      // locatie/planning/settelefoon/contactKlant uit het voorstel → { aan, … }
  const [bronnenTonen, setBronnenTonen] = useState(true);     // PL ziet bronverwijzingen als links; crew-weergave en PDF niet
  const [briefingActie, setBriefingActie] = useState(null);   // 'pdf' | 'mail' | null
  const { accounts: msalAccounts } = useMsal();
  const AI_VELD_NAAR_AIRTABLE = { algemene_opmerkingen: 'Algemene opmerkingen', props: 'Props', styling: 'Styling', visagie: 'Visagie', lunch: 'Lunch', reminder: 'Reminder', kledingvoorschriften: 'Kledingvoorschriften' };
  // Leren van correcties (wiki-feedback 26 sep): welke AI-voorstellen de PL overnam. Past hij/zij
  // zo'n veld daarna aan (in de dialoog of later op deze pagina), dan gaat voorstel → opgeslagen
  // naar de server; de volgende analyse van deze PL krijgt die als voorbeeld mee.
  const aiOvergenomenRef = useRef({});
  const meldCorrecties = (opgeslagen) => {
    const lijst = correctiesVoor(aiOvergenomenRef.current, opgeslagen);
    if (!lijst.length) return;
    authFetch(`${BACKEND_URL}/Api/callsheets/${id}/ai-correcties`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correcties: lijst }) })
      .catch(() => {}); // leren is bijzaak: een fout hier mag het opslaan niet storen
  };
  const aiAantalAan = Object.values(aiKeuze).filter((v) => v.aan).length + Object.values(aiExtraKeuze).filter((v) => v.aan).length;
  const AI_LABELS = { algemene_opmerkingen: '📝 Algemene opmerkingen', props: '📦 Props', styling: '🎨 Styling', visagie: '💄 Visagie & haar', lunch: '🍴 Lunch / catering', reminder: '⏰ Reminder', kledingvoorschriften: '👕 Kledingvoorschriften', briefing_crew: '🎥 Briefing crew' };
  // AI voorbereiden draait op de achtergrond (tool-feedback #63, Boy 6 okt: "kun je niet cancellen,
  // duurt soms heel lang"). Starten geeft meteen een job; lege velden worden per klaar deel
  // ingevuld, wat al gevuld was (of wat je intussen typte) wordt een voorstel.
  const [aiIngevuld, setAiIngevuld] = useState([]);              // [{ blok, label, herstel, hersteld }]
  const [aiVoorstelBlokken, setAiVoorstelBlokken] = useState([]); // blokken die als voorstel klaarliggen
  const [aiInvulFouten, setAiInvulFouten] = useState([]);
  const editingFieldRef = useRef(null);
  editingFieldRef.current = editingField;
  const briefingLokaalRef = useRef(false);   // briefing open in de editor of nog niet opgeslagen tekst
  briefingLokaalRef.current = briefingBewerk || (!!String(briefingTekst || '').trim() && briefingTekst !== (extra.briefing || ''));
  // Welke callsheet nu open is: een late verversing voor een vorige callsheet mag de state van de
  // huidige niet overschrijven (Codex r3).
  const openIdRef = useRef(id);
  openIdRef.current = id;
  const verversAlsNogOpen = async (csId) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${csId}`);
      if (!r.ok) return;
      const d = await r.json();
      if (openIdRef.current === csId) setCallsheetData(metUniekeCrew(d));
    } catch { /* volgende verversing */ }
  };
  // Na ongedaan maken: alleen de teruggezette blokken bijwerken. Een open of nog niet opgeslagen
  // briefing (of begin-/eindtijd in de editor) blijft altijd staan (Codex r4).
  // `open`: wat open/onopgeslagen stond VÓÓR het terugzetten (dat zelf wijzigt extra.briefing; Codex r5).
  const verversNaHerstel = async (blokken = [], open = []) => {
    const csId = id;
    setAiIngevuld((l) => [...l]);
    if (!blokken.length) return;
    await verversAlsNogOpen(csId);
    if (!blokken.includes('briefing_crew')) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${csId}/extra`);
      const e = r.ok ? await r.json() : null;
      if (!e || openIdRef.current !== csId) return;
      setExtra((x) => ({ ...x, briefing: e.briefing || '', briefingBronnen: e.briefingBronnen || [] }));
      if (naHerstelBijwerken(blokken, open).briefing) setBriefingTekst(e.briefing || '');
    } catch { /* volgende verversing */ }
  };
  // Stil lezen/schrijven (geen foutbanner, geen state): de AI-verwerking kan nog lopen terwijl je al
  // een andere callsheet open hebt; state wordt alleen bijgewerkt als deze callsheet nog open is.
  const haalCallsheetStil = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`);
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };
  const haalExtraStil = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`);
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };
  const zetExtraUit = (d) => setExtra({ begintijd: d.begintijd || '', eindtijd: d.eindtijd || '', tijdenBron: d.tijdenBron, briefing: d.briefing || '', briefingBronnen: d.briefingBronnen || [] });
  const aiBewerkt = () => [blokVanVeld(editingFieldRef.current), briefingLokaalRef.current ? 'briefing_crew' : null].filter(Boolean);
  // Geeft false terug als het deel (nog) niet goed is ingevuld: dan geeft de hook de claim terug en
  // probeert het later opnieuw; wat al is ingevuld telt dan als gevuld en wordt niet nog eens gedaan.
  const verwerkAiDeel = async (deel, res, job, { actueel } = {}) => {
    const nogHier = () => (!actueel || actueel()) && (!job?.callsheetId || job.callsheetId === id);
    if (!nogHier()) return false;
    const vers = await haalCallsheetStil();
    const versExtra = await haalExtraStil();
    if (!vers || !versExtra || !nogHier()) return false;   // herlezen mislukt of weg: niets op oude gegevens invullen
    const opties = {};
    const nu = huidigeBlokken(vers, versExtra, opties);
    const persoon = res.settelefoon ? zoekPersoon(res.settelefoon) : null; // eslint-disable-line no-use-before-define
    const { vullen, overslaan } = kiesInvulling(res.invullen, nu, { bewerkt: aiBewerkt(), persoonGevonden: !!persoon });
    const bewaarExtraStil = async (velden) => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden) });
        if (!r.ok) return false;
        const d = await r.json();
        if (nogHier()) zetExtraUit(d);
        return true;
      } catch { return false; }
    };
    const { ingevuld, fouten, overgeslagen } = await vulBlokken(vullen, res, {
      fetcher: authFetch, base: BACKEND_URL, id, extra: versExtra, actueel: nogHier,
      bewaarExtra: bewaarExtraStil, bronnen: job?.resultaat?.bronnen || [], zoekPersoon, haalExtra: haalExtraStil, // eslint-disable-line no-use-before-define
      haalCallsheet: haalCallsheetStil, haalVelden: async () => (await haalCallsheetStil())?.callsheet?.fields,
      controleer: maakControle({ haalCallsheet: haalCallsheetStil, haalExtra: haalExtraStil, opmerkingenTekst: opties.opmerkingenTekst, bewerkt: aiBewerkt }),
    });
    // Bij een herhaalde poging: wat de AI eerder al invulde is geen 'voorstel'.
    const alIngevuld = (b) => !!String(res.velden?.[b] || '').trim() && String(nu[b] || '').trim() === String(res.velden[b]).trim();
    const mislukt = vullen.filter((b) => !ingevuld.some((x) => x.blok === b));
    if (nogHier()) {
      if (ingevuld.some((x) => x.blok === 'briefing_crew')) setBriefingTekst(res.velden.briefing_crew);
    ingevuld.forEach((x) => { if (res.velden?.[x.blok] != null) aiOvergenomenRef.current[x.blok] = res.velden[x.blok]; });
      setAiIngevuld((l) => [...l, ...ingevuld]);
      setAiVoorstelBlokken((l) => [...new Set([...l, ...(res.voorstellen || []), ...overslaan.filter((b) => !alIngevuld(b)), ...overgeslagen, ...mislukt])]);
      if (fouten.length) setAiInvulFouten((l) => [...l, ...fouten]);
      biedDeelOngedaanAan(ingevuld, verversNaHerstel, { bewerkt: aiBewerkt });
      if (ingevuld.length) verversAlsNogOpen(id);
    }
    return fouten.length === 0;
  };
  const aiKlaar = (job) => {
    if (!job.opnieuw) return;
    if (job.status === 'klaar') toonVoorstel(voorstelUitJob(job), { opnieuw: true }); // eslint-disable-line no-use-before-define
    else if (job.status === 'fout') toast.error(job.fout || 'AI voorbereiden mislukt');
  };
  const ai = useAiVoorbereiden({ callsheetId: id, fetcher: authFetch, base: BACKEND_URL, onDeel: verwerkAiDeel, onKlaar: aiKlaar });
  const aiBriefingBusy = ai.bezig;
  const aiOpnieuwBezig = ai.bezig && !!ai.job?.opnieuw;
  const herstelAiBlok = async (x) => {
    const open = aiBewerkt();
    try {
      await herstelBlok(x, open);
      toast.success(`${x.label} teruggezet`);
      verversNaHerstel([x.blok], open);
    } catch (e) { toast.error(e.message); setAiIngevuld((l) => [...l]); }
  };
  const aiPunten = ai.job && !ai.job.opnieuw ? ((ai.job.resultaat?.delen?.callsheet?.conflicten || []).length + (ai.job.resultaat?.delen?.callsheet?.vragen || []).length) : 0;
  // Na een refresh staat de lijst niet meer in de state: dan uit de job (wat bij gevulde velden lag).
  const aiVoorstelLijst = aiVoorstelBlokken.length || !ai.job?.hervat ? aiVoorstelBlokken
    : [...new Set(['callsheet', 'briefing'].flatMap((d) => ai.job.resultaat?.delen?.[d]?.voorstellen || []))];
  const bekijkAiVoorstel = () => { if (ai.job) toonVoorstel(voorstelUitJob(ai.job, { alleen: aiVoorstelLijst })); }; // eslint-disable-line no-use-before-define
  // Andere callsheet geopend (zelfde component): AI-samenvatting van de vorige hoort hier niet.
  useEffect(() => { setAiIngevuld([]); setAiVoorstelBlokken([]); setAiInvulFouten([]); }, [id]);

  // Vanuit de aanmaak-wizard (?ai=1): meteen voorbereiden zodra de callsheet geladen is.
  const aiAutoGestart = useRef(false);
  useEffect(() => {
    if (!callsheetData || aiAutoGestart.current || searchParams.get('ai') !== '1') return;
    aiAutoGestart.current = true;
    // ?ai=1 uit de adresbalk: een refresh mag niet nog een voorbereiding starten.
    try { const u = new URL(window.location.href); u.searchParams.delete('ai'); window.history.replaceState(window.history.state, '', u.toString()); } catch { /* geen URL-API */ }
    vraagAiBriefing(); // eslint-disable-line no-use-before-define
  }, [callsheetData, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const vraagAiBriefing = async ({ opnieuw = false } = {}) => {
    try {
      if (opnieuw) {
        const keuzes = Object.entries(aiConflictKeuze).filter(([, w]) => w).map(([i, w]) => `${aiBriefing?.conflicten?.[i]?.onderwerp || 'keuze'}: ${w}`);
        await ai.start({ opnieuw: true, aanwijzingen: aiAanwijzingen, extraBron: aiExtraBron, keuzes, vorigVoorstel: { velden: Object.fromEntries(Object.entries(aiKeuze).map(([k, v]) => [k, v.tekst])) } });
        return;
      }
      setAiIngevuld([]); setAiVoorstelBlokken([]); setAiInvulFouten([]);
      await ai.start({ leegBijStart: leegBijStart(huidigeBlokken(callsheetData, extra)) });
    } catch (e) { toast.error(e.message); }
  };
  // Het voorstel in de dialoog: alleen wat nog niet automatisch is ingevuld (of een herhaalde vraag).
  const toonVoorstel = (d, { opnieuw = false } = {}) => {
    const heeftIets = Object.keys(d.velden || {}).length || d.locatie || (d.planning || []).length || d.settelefoon || d.contactKlant
      || (d.conflicten || []).length || (d.vragen || []).length;
    if (!heeftIets) { toast(d.melding || 'Geen voorstel: er zijn nog geen bronnen aan dit project gekoppeld.'); return; }
    const nu = huidigeBlokken(callsheetData, extra);
    const keuze = {};
    Object.entries(d.velden || {}).forEach(([k, tekst]) => {
      const bestaand = nu[k] || '';
      const vorige = aiKeuze[k];
      keuze[k] = { aan: opnieuw && vorige ? vorige.aan : !String(bestaand).trim(), tekst, bestaand };
    });
    const ck = {};
    (d.conflicten || []).forEach((c, i) => { ck[i] = standaardConflictKeuze(c); });
    setAiConflictStart(ck);
    const cf = callsheetData?.callsheet?.fields || {};
    const ek = {};
    if (d.locatie) ek.locatie = { aan: !(callsheetData?.locaties || []).length, ...d.locatie };
    if ((d.planning || []).length) ek.planning = { aan: !(callsheetData?.planning || []).length, rows: d.planning, vervang: false };
    if (d.settelefoon) { const p = zoekPersoon(d.settelefoon); ek.settelefoon = { aan: !!p && !cf['Naam (from Contactpersoon Settelefoon)']?.[0], naam: d.settelefoon, persoon: p }; } // eslint-disable-line no-use-before-define
    if (d.contactKlant) ek.contactKlant = { aan: !(cf['Contactpersoon (handmatig)'] || cf['Contactgegevens klant telefoon (Handmatig)']), ...d.contactKlant };
    setAiExtraKeuze(ek);
    setAiConflictKeuze(ck); setAiKeuze(keuze); setAiBriefing(d); setAiTab(0);
    if (!opnieuw) { setAiAanwijzingen(''); setAiExtraBron(''); }
  };
  const neemAiOver = async () => {
    const gekozen = Object.entries(aiKeuze).filter(([, v]) => v.aan && String(v.tekst || '').trim());
    const ek = aiExtraKeuze;
    const extraAan = ['locatie', 'planning', 'settelefoon', 'contactKlant'].filter((k) => ek[k]?.aan);
    if (!gekozen.length && !extraAan.length) { setAiBriefing(null); return; }
    const velden = {};
    let briefing = null;
    gekozen.forEach(([k, v]) => { if (k === 'briefing_crew') briefing = v.tekst; else if (AI_VELD_NAAR_AIRTABLE[k]) velden[AI_VELD_NAAR_AIRTABLE[k]] = v.tekst; });
    if (ek.settelefoon?.aan && ek.settelefoon.persoon) velden['Contactpersoon Settelefoon'] = [ek.settelefoon.persoon.id];
    if (ek.contactKlant?.aan) {
      if (ek.contactKlant.naam) velden['Contactpersoon (handmatig)'] = ek.contactKlant.naam;
      if (ek.contactKlant.telefoon) velden['Contactgegevens klant telefoon (Handmatig)'] = ek.contactKlant.telefoon;
    }
    const json = { 'Content-Type': 'application/json' };
    let planningWaarschuwing = null;
    try {
      if (Object.keys(velden).length) {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, { method: 'PATCH', headers: json, body: JSON.stringify(velden) });
        if (!r.ok) throw new Error(`Velden opslaan mislukt (HTTP ${r.status})`);
      }
      if (briefing !== null) { if (!(await bewaarExtra({ briefing, briefingBronnen: aiBriefing?.bronnen || [] }))) throw new Error('Briefing opslaan mislukt'); setBriefingTekst(briefing); }
      if (ek.locatie?.aan) {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheet-locaties`, { method: 'POST', headers: json, body: JSON.stringify({ callsheetId: id, naam: ek.locatie.naam, straat: ek.locatie.straat, postcode: ek.locatie.postcode, plaats: ek.locatie.plaats, opmerkingen: ek.locatie.opmerkingen }) });
        if (!r.ok) throw new Error(`Locatie toevoegen mislukt (HTTP ${r.status})`);
      }
      if (ek.planning?.aan) {
        // Eerst de nieuwe regels, dan pas de oude weg; mislukt een POST halverwege, dan halen we de
        // net aangemaakte regels weer weg zodat opnieuw proberen geen duplicaten geeft (Codex #4).
        const oude = ek.planning.vervang ? (callsheetData?.planning || []).map((p) => p.id) : [];
        const nieuw = [];
        try {
          for (const row of ek.planning.rows) {
            const [h, mnt] = String(row.tijd).split(':').map(Number);
            if (!(h >= 0 && h <= 23 && mnt >= 0 && mnt <= 59)) continue;
            const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning`, { method: 'POST', headers: json, body: JSON.stringify({ callsheetId: id, tijd: h * 3600 + mnt * 60, duur: row.duur_min || 0, omschrijving: row.omschrijving || '', locatie: row.locatie || '' }) });
            if (!r.ok) throw new Error(`Planningsregel toevoegen mislukt (HTTP ${r.status})`);
            const d = await r.json().catch(() => ({}));
            const nid = d?.record?.id || d?.id;
            if (nid) nieuw.push(nid);
          }
        } catch (e) {
          for (const nid of nieuw) await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${nid}`, { method: 'DELETE' }).catch(() => {});
          fetchCallsheet(false);
          throw e;
        }
        let nietWeg = 0;
        for (const pid of oude) {
          const r = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${pid}`, { method: 'DELETE' });
          if (!r.ok) nietWeg += 1;
        }
        if (nietWeg) planningWaarschuwing = `${nietWeg} oude planningsregel${nietWeg === 1 ? '' : 's'} kon niet worden verwijderd — controleer de planning`;
      }
      gekozen.forEach(([k]) => { if (aiBriefing?.velden?.[k] != null) aiOvergenomenRef.current[k] = aiBriefing.velden[k]; });
      setAiVoorstelBlokken((l) => l.filter((b) => !gekozen.some(([k]) => k === b) && !extraAan.includes(b)));
      meldCorrecties(Object.fromEntries(gekozen.map(([k, v]) => [k, v.tekst])));
      const n = gekozen.length + extraAan.length;
      if (planningWaarschuwing) toast(`${n} onderdeel${n === 1 ? '' : 'en'} overgenomen, maar: ${planningWaarschuwing}`, { icon: '⚠️', duration: 8000 });
      else toast.success(`${n} onderdeel${n === 1 ? '' : 'en'} overgenomen`);
      setAiBriefing(null);
      fetchCallsheet(false);
    } catch (e) { toast.error(e.message); }
  };
  const haalBriefingPdf = async () => {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/briefing.pdf`);
    if (!r.ok) throw new Error(`PDF maken mislukt (HTTP ${r.status})`);
    return r.blob();
  };
  const briefingBestandsnaam = () => `Briefing ${String(callsheetData?.callsheet?.fields?.Titel || 'draaidag').replace(/[\\/:*?"<>|]+/g, ' ').trim()}.pdf`;
  const downloadBriefingPdf = async () => {
    setBriefingActie('pdf');
    try {
      const url = URL.createObjectURL(await haalBriefingPdf());
      const a = document.createElement('a'); a.href = url; a.download = briefingBestandsnaam(); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
    finally { setBriefingActie(null); }
  };
  // Concept in Outlook (map Concepten) aan de crew, met de briefing-PDF als bijlage.
  // Draft-only: de PL leest na en verstuurt zelf (Joris 18 sep).
  const conceptMailCrew = async () => {
    setBriefingActie('mail');
    try {
      const [blob, ontvR] = await Promise.all([
        haalBriefingPdf(),
        authFetch(`${BACKEND_URL}/Api/callsheets/${id}/crew-ontvangers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }),
      ]);
      const ontv = ontvR.ok ? await ontvR.json() : { ontvangers: [], onbekend: [] };
      const base64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1]); fr.onerror = rej; fr.readAsDataURL(blob); });
      const f = callsheetData?.callsheet?.fields || {};
      const titel = f.Titel || 'de draaidag';
      const datum = f['Datum (handmatig)'] || f.Datum || '';
      const voornamen = (ontv.ontvangers || []).map((o) => String(o.naam || '').split(' ')[0]).filter(Boolean);
      const mijnNaam = String(msalAccounts?.[0]?.name || '').split(' ')[0];
      const body = [
        `Hoi ${voornamen.length ? voornamen.join(', ') : 'allemaal'},`, '',
        `In de bijlage de briefing voor ${titel}${datum ? ` op ${datum}` : ''}: het verhaal achter de film, wat er zeker gefilmd moet worden en waar je op moet letten.`, '',
        `Het callsheet met tijden, locatie en contactgegevens staat hier: ${window.location.origin}/callsheets/${id}`, '',
        'Vragen? Bel of app me gerust.', '', 'Groet,', mijnNaam,
      ].join('\n');
      const d = await createDraftMessage({
        subject: `Briefing ${titel}${datum ? ` – ${datum}` : ''}`, body,
        to: (ontv.ontvangers || []).map((o) => o.email), attachments: [{ name: briefingBestandsnaam(), base64 }],
      });
      const onbekend = (ontv.onbekend || []).length ? ` Geen e-mail bekend van: ${ontv.onbekend.join(', ')}.` : '';
      toast.success((t) => (
        <span>Concept staat in Outlook ({(ontv.ontvangers || []).length} ontvangers).{onbekend}{' '}
          {d.webLink && <a href={d.webLink} target="_blank" rel="noreferrer" onClick={() => toast.dismiss(t.id)}>Openen</a>}</span>
      ), { duration: 10000 });
    } catch (e) { toast.error(e.message); }
    finally { setBriefingActie(null); }
  };
  // Briefing → Script breakdown / shotlist-planner als nieuw script (zonder bronverwijzingen).
  const naarShotlist = () => {
    const f = callsheetData?.callsheet?.fields || {};
    const pnRaw = callsheetData?.draaidag?.[0]?.fields?.['Projectnummer (from Project)'];
    const pn = Array.isArray(pnRaw) ? pnRaw[0] : pnRaw;
    navigate('/script-breakdown', { state: { prefill: { title: `Shotlist ${f.Titel || 'draaidag'}`, script: stripBronRefs(briefingTekst), projectNumber: pn != null ? String(pn) : '' } } });
  };
  // Settelefoon uit het AI-voorstel koppelen aan een Personen-record (exacte naam, anders unieke voornaam).
  const zoekPersoon = (naam) => {
    const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
    const n = norm(naam);
    if (!n) return null;
    const lijst = (personen || []).filter((p) => !p._source);
    const naamVan = (p) => norm(p.fields?.['Volledige naam'] || p.fields?.Naam);
    const exact = lijst.find((p) => naamVan(p) === n);
    if (exact) return exact;
    const kandidaten = lijst.filter((p) => naamVan(p).split(' ')[0] === n.split(' ')[0]);
    return kandidaten.length === 1 ? kandidaten[0] : null;
  };
  useEffect(() => {
    if (!id) return undefined;
    let actief = true;
    authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!actief || !d) return;
        setExtra({ begintijd: d.begintijd || '', eindtijd: d.eindtijd || '', tijdenBron: d.tijdenBron, briefing: d.briefing || '', briefingBronnen: d.briefingBronnen || [] });
        setBriefingTekst(d.briefing || '');
        // Slotjes: de server is de bron. Staan er alleen nog oude slotjes in deze browser, dan gaan
        // die eenmalig naar de server, zodat collega's en andere apparaten ze ook zien.
        // Heeft de server ooit slotjes gehad (ook een bewust lege set), dan is hij leidend; alleen als
        // hij nog nooit iets kreeg gaan oude slotjes uit deze browser één keer mee.
        const lokaal = leesLokaleSloten(id);
        if (d.vastePlanningGezet || !lokaal.size) {
          zetSloten(Array.isArray(d.vastePlanning) ? d.vastePlanning : []);
        } else {
          bewaarSloten({ vastZetten: [...lokaal], migratie: true })
            .then((uit) => { if (actief && Array.isArray(uit.vastePlanning)) zetSloten(uit.vastePlanning); })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => { actief = false; };
  }, [id, bewaarSloten, zetSloten]);
  // Crew informeren (Joris, 15 sep): melding in intern + update in de crew-app standaard aan,
  // e-mail standaard uit. De PL kiest per keer.
  const [informeer, setInformeer] = useState(null);   // null | { bericht, webapp, crewApp, mail, bezig }
  // Bijlagen bij de crew-mail (21 okt): overzicht uit het blok, ook getoond in het venster.
  const [bijlagenOverzicht, setBijlagenOverzicht] = useState(null);
  // Dubbelcheck vóór versturen (Joris, 3 nov): "Versturen" opent eerst de checklist; pas na
  // "Klopt, versturen" gaat het bestaande vangnet (5 s Annuleren bij e-mail) lopen.
  const [verzendCheck, setVerzendCheck] = useState(false);
  const laadCrewOntvangers = useCallback(async () => {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/crew-ontvangers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [id]);
  const verstuurInformeer = async (bevestigd) => {
    if (!informeer || informeer.bezig) return;
    const keuze = informeer;
    setInformeer((x) => ({ ...x, bezig: true }));
    // Wie de PL in de checklist zag, krijgt het: vlak vóór het echte verzoek (ook na de 5 s)
    // nog eens ophalen; anders veranderd of niet vast te stellen → niets versturen.
    const vooraf = async () => {
      let nu;
      try { nu = await laadCrewOntvangers(); } catch { throw new Error('Ontvangers niet te controleren — er is niets verstuurd'); }
      if (!zelfdeOntvangers(nu, bevestigd?.ontvangers)) throw new Error('De ontvangers zijn intussen veranderd — er is niets verstuurd. Controleer de checklist opnieuw.');
    };
    const verwachteOntvangers = (bevestigd?.ontvangers?.ontvangers || []).map((o) => o.email).filter(Boolean);
    const r = await informeerCrew({ callsheetId: id, keuze, overzicht: bijlagenOverzicht, onStart: () => setInformeer(null), vooraf, verwachteOntvangers });
    if (r.ok) fetchCallsheet(false);
    else setInformeer({ ...keuze, bezig: false });
  };
  const bewaarExtra = async (velden) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setExtra({ begintijd: d.begintijd || '', eindtijd: d.eindtijd || '', tijdenBron: d.tijdenBron, briefing: d.briefing || '', briefingBronnen: d.briefingBronnen || [] });
      return true;
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); return false; }
  };
  
  // Fetch personen (team members) for crew selection — /crew bevat ook freelancers/
  // stagiairs (externen), niet alleen interne medewerkers.
  const fetchPersonen = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/personen/crew`);
      if (response.ok) {
        const data = await response.json();
        // API returns { people, records } - use records for Autocomplete (has fields property)
        setPersonen(data.records || []);
      }
    } catch (err) {
      console.error('Error fetching personen:', err);
    }
  }, []);
  
  // Fetch casting options for cast selection
  const fetchCasting = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/casting`);
      if (response.ok) {
        const data = await response.json();
        setCastingOptions(data || []);
      }
    } catch (err) {
      console.error('Error fetching casting:', err);
    }
  }, []);
  
  // Fetch existing locations for location selection
  const fetchLocaties = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/locaties`);
      if (response.ok) {
        const data = await response.json();
        setExistingLocaties(data || []);
      }
    } catch (err) {
      console.error('Error fetching locaties:', err);
    }
  }, []);
  
  // Fetch callsheet data (with loading state)
  const fetchCallsheet = useCallback(async (showLoading = true) => {
    if (!id) return;
    
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`);
      if (!response.ok) throw new Error('Callsheet niet gevonden');
      
      const data = metUniekeCrew(await response.json());
      setCallsheetData(data);
      setPdfStatus(data.callsheet?.fields?.StatusPDFWarning || data.callsheet?.fields?.StatusPDF);
      return data;
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id]);
  
  // Soft refresh - update data without showing loading spinner
  const refreshData = useCallback(() => {
    fetchCallsheet(false);
  }, [fetchCallsheet]);

  const refreshDataAndRecalculate = useCallback(async () => {
    const data = await fetchCallsheet(false);
    const items = data?.planning ? [...data.planning] : [];
    if (items.length > 0) {
      const ordered = items.sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));
      await recalculatePlanningTimes(ordered);
    }
  }, [fetchCallsheet]);
  
  useEffect(() => {
    fetchCallsheet(true);
    fetchPersonen();
    fetchCasting();
    fetchLocaties();
    // Fetch weather in background
    if (id) {
      authFetch(`${BACKEND_URL}/Api/callsheets/${id}/weather`)
        .then(r => r.ok ? r.json() : { weather: '' })
        .then(d => setWeather(d.weather || ''))
        .catch(() => {});
    }
  }, [fetchCallsheet, fetchPersonen, fetchCasting, fetchLocaties, id]);

  // Net aangemaakt (?fresh=1): de standaard-planning wordt async door een Airtable-
  // automation toegevoegd. Doe een paar stille her-fetches zodat die vanzelf verschijnt
  // (geen handmatige refresh nodig).
  useEffect(() => {
    if (!id || searchParams.get('fresh') !== '1') return;
    const timers = [2500, 5000, 8000].map(ms => setTimeout(() => fetchCallsheet(false), ms));
    return () => timers.forEach(clearTimeout);
  }, [id, searchParams, fetchCallsheet]);

  // Update callsheet field
  const updateField = async (fieldName, value) => {
    // Optimistisch alleen dit veld bijwerken — geen volledige paginarefresh.
    setCallsheetData(prev => prev ? {
      ...prev,
      callsheet: { ...prev.callsheet, fields: { ...prev.callsheet.fields, [fieldName]: value } },
    } : prev);
    setEditingField(null);
    try {
      // Alleen na een geslaagde opslag leren van de correctie.
      const aiKey = Object.keys(AI_VELD_NAAR_AIRTABLE).find((k) => AI_VELD_NAAR_AIRTABLE[k] === fieldName);
      await slaVeldOp({ fetcher: authFetch, url: `${BACKEND_URL}/Api/callsheets/${id}`, veld: fieldName, waarde: value,
        naSucces: aiKey ? () => meldCorrecties({ [aiKey]: value }) : null });
    } catch (err) {
      setError(err.message);
      fetchCallsheet(false);   // bij fout: herstel vanuit server
    }
  };
  
  // Delete crew member
  const deleteCrew = async (crewId) => {
    if (!(await bevestig({ titel: 'Crewlid verwijderen?', tekst: 'Van deze callsheet. Dit is niet ongedaan te maken.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-crew/${crewId}`, { method: 'DELETE' });
      await verwijderCrewDubbelen((callsheetData?.crew || []).find((c) => c.id === crewId)?._dubbelen);
      fetchCallsheet(false);
    } catch (err) {
      await fetchCallsheet(false);   // toon wat er wél is gebeurd; die verversing wist de fout, dus daarna zetten
      setError(err.message);
    }
  };

  // Zet de calltijd van alle crew gelijk aan de eerste call-tijd van de dag (eerste
  // planning-item op tijd). Daarna kan per crewlid de tijd nog los aangepast worden.
  const setAllCrewToFirstCalltime = async () => {
    const planningItems = callsheetData?.planning || [];
    const crewItems = callsheetData?.crew || [];
    if (!planningItems.length) { setError('Geen planning — voeg eerst een eerste call-tijd toe'); return; }
    if (!crewItems.length) return;
    const firstTime = [...planningItems].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0))[0]?.fields?.Tijd;
    if (firstTime == null) return;
    if (!(await bevestig({ titel: 'Calltijd voor alle crew gelijkzetten?', tekst: `Alle ${crewItems.length} crewleden krijgen ${formatDuration(firstTime)}. Je kunt daarna per persoon aanpassen.`, knop: 'Gelijkzetten' }))) return;
    // Optimistisch
    setCallsheetData(prev => prev ? ({
      ...prev,
      crew: (prev.crew || []).map(c => ({ ...c, fields: { ...c.fields, Calltijd: firstTime } })),
    }) : prev);
    try {
      await Promise.all(crewItems.map(c =>
        authFetch(`${BACKEND_URL}/Api/callsheet-crew/${c.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Calltijd: firstTime }),
        })
      ));
    } catch (err) {
      fetchCallsheet(false);
      setError(err.message);
    }
  };

  // Zelfde als crew, maar voor cast: zet alle cast op de eerste call-tijd van de dag.
  const setAllCastToFirstCalltime = async () => {
    const planningItems = callsheetData?.planning || [];
    const castItems = callsheetData?.cast || [];
    if (!planningItems.length) { setError('Geen planning — voeg eerst een eerste call-tijd toe'); return; }
    if (!castItems.length) return;
    const firstTime = [...planningItems].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0))[0]?.fields?.Tijd;
    if (firstTime == null) return;
    if (!(await bevestig({ titel: 'Calltijd voor alle cast gelijkzetten?', tekst: `Alle ${castItems.length} cast krijgen ${formatDuration(firstTime)}. Je kunt daarna per persoon aanpassen.`, knop: 'Gelijkzetten' }))) return;
    setCallsheetData(prev => prev ? ({
      ...prev,
      cast: (prev.cast || []).map(c => ({ ...c, fields: { ...c.fields, Calltijd: firstTime } })),
    }) : prev);
    try {
      await Promise.all(castItems.map(c =>
        authFetch(`${BACKEND_URL}/Api/callsheet-cast/${c.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Calltijd: firstTime }),
        })
      ));
    } catch (err) {
      fetchCallsheet(false);
      setError(err.message);
    }
  };

  // Sorteer crew/cast op handmatige Volgorde (slepen); val terug op calltijd zolang
  // er nog geen volgorde is gezet. Volgorde leeft op het callsheet-koppelrecord, dus
  // per callsheet onafhankelijk (een persoon in meerdere callsheets botst niet).
  const sortByVolgorde = (rows) => [...(rows || [])].sort((a, b) => {
    const va = a.fields?.Volgorde ?? Infinity;
    const vb = b.fields?.Volgorde ?? Infinity;
    if (va !== vb) return va - vb;
    const ca = a.fields?.Calltijd ?? Infinity, cb = b.fields?.Calltijd ?? Infinity;
    if (ca !== cb) return ca - cb;
    // Stabiel: aanmaakvolgorde als laatste tie-break, anders husselt de lijst.
    return String(a.createdTime || '').localeCompare(String(b.createdTime || ''));
  });

  // Sla een nieuwe volgorde op: hernummer Volgorde 0..n en PATCH elke rij.
  const persistRowOrder = async (orderedRows, key, endpoint) => {
    setCallsheetData(prev => prev ? ({
      ...prev,
      [key]: orderedRows.map((r, i) => ({ ...r, fields: { ...r.fields, Volgorde: i } })),
    }) : prev);
    try {
      await Promise.all(orderedRows.map((r, i) =>
        authFetch(`${BACKEND_URL}/Api/${endpoint}/${r.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Volgorde: i }),
        })
      ));
    } catch (err) {
      fetchCallsheet(false);
      setError(err.message);
    }
  };

  // Drop-handler voor slepen: verplaats de gesleepte rij naar de positie van targetId.
  const handleRowDrop = (key, endpoint, sortedRows, targetId) => {
    if (!draggedRow || draggedRow.key !== key) { setDraggedRow(null); return; }
    const fromIdx = sortedRows.findIndex(r => r.id === draggedRow.id);
    const toIdx = sortedRows.findIndex(r => r.id === targetId);
    setDraggedRow(null);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const arr = [...sortedRows];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    persistRowOrder(arr, key, endpoint);
  };

  // Delete planning item
  // Dupliceer een planning-regel (zelfde velden, nieuwe rij).
  const duplicatePlanning = async (p) => {
    try {
      const f = p.fields || {};
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callsheetId: id,
          tijd: f.Tijd || 0, duur: f['Duur (minuten)'] || 0,
          omschrijving: f.Omschrijving || '', locatie: f.Locatie || '',
          cast: f.Cast || '', opmerkingen: f.Opmerkingen || '',
        }),
      });
      await refreshData();
    } catch (err) { setError(err.message); }
  };

  const deletePlanning = async (planningId) => {
    if (!(await bevestig({ titel: 'Planning-item verwijderen?', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      // Optimistically remove from UI to avoid full refresh
      setCallsheetData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          planning: (prev.planning || []).filter(p => p.id !== planningId)
        };
      });
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${planningId}`, { method: 'DELETE' });
      await refreshDataAndRecalculate();
    } catch (err) {
      // Rollback by reloading if something failed
      refreshData();
      setError(err.message);
    }
  };

  // Inline bewerken van planning-cellen (Omschrijving / Duur / Opmerkingen).
  const [editCell, setEditCell] = useState(null); // { id, field }
  const [editVal, setEditVal] = useState('');

  const beginEditCell = (p, field) => {
    setEditCell({ id: p.id, field });
    // Tijd staat als seconden in de DB maar tonen/bewerken we als HH:MM.
    setEditVal(field === 'Tijd' ? formatDuration(p.fields?.Tijd) : String(p.fields?.[field] ?? ''));
  };

  const savePlanningCell = async () => {
    if (!editCell) return;
    const { id, field } = editCell;
    const value = field === 'Duur (minuten)' ? (parseInt(editVal, 10) || 0)
      : field === 'Tijd' ? parseDuration(editVal)
      : editVal;
    setEditCell(null);
    // Optimistische update
    setCallsheetData(prev => prev ? ({
      ...prev,
      planning: (prev.planning || []).map(p => p.id === id ? { ...p, fields: { ...p.fields, [field]: value } } : p),
    }) : prev);
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
    } catch (err) {
      refreshData();
      setError(err.message);
    }
  };

  // Render een planning-cel: klik → inline TextField, opslaan bij blur/Enter, Esc = annuleren.
  // Direct een planning-veld zetten (bv. Locatie-dropdown), optimistisch + PATCH.
  const setPlanningField = async (planningId, field, value) => {
    setCallsheetData(prev => prev ? ({
      ...prev,
      planning: (prev.planning || []).map(p => p.id === planningId ? { ...p, fields: { ...p.fields, [field]: value } } : p),
    }) : prev);
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-planning/${planningId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
    } catch (err) { refreshData(); setError(err.message); }
  };

  const renderPlanningCell = (p, field, display) => {
    const editing = editCell && editCell.id === p.id && editCell.field === field;
    if (editing) {
      return (
        <TextField
          value={editVal}
          autoFocus
          variant="standard"
          size="small"
          type={field === 'Duur (minuten)' ? 'number' : 'text'}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={savePlanningCell}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); savePlanningCell(); }
            if (e.key === 'Escape') setEditCell(null);
          }}
          sx={{ minWidth: field === 'Omschrijving' ? 160 : 70 }}
        />
      );
    }
    return (
      <Box
        onClick={() => beginEditCell(p, field)}
        title="Klik om te bewerken"
        sx={{ cursor: 'text', minHeight: 22, px: 0.5, borderRadius: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
      >
        {display}
      </Box>
    );
  };

  // Move planning item up or down and recalculate times
  const movePlanningItem = async (itemId, direction) => {
    if (!planning || planning.length < 2) return;
    
    // Sort planning by time first
    const sortedPlanning = [...planning].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));
    const currentIndex = sortedPlanning.findIndex(p => p.id === itemId);
    
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === sortedPlanning.length - 1) return;
    
    // Swap items
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const temp = sortedPlanning[currentIndex];
    sortedPlanning[currentIndex] = sortedPlanning[newIndex];
    sortedPlanning[newIndex] = temp;
    
    // Recalculate times based on first item's time and durations
    await recalculatePlanningTimes(sortedPlanning);
  };

  // Recalculate all planning times based on order and durations
  const recalculatePlanningTimes = async (orderedPlanning = null) => {
    const itemsToUpdate = orderedPlanning || [...planning].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));

    if (itemsToUpdate.length === 0) return;

    // 🔒 Vastgezette regels houden hun begintijd; wat erna komt sluit aan op het
    // einde van de vaste regel (Joris 18 sep: na een vaste lunch schuiven de
    // middagopnames mee). De cascade start bij de eerste NIET-vaste regel.
    const sloten = lockedRef.current;   // actueel, ook als deze functie uit een oude closure komt
    const firstFree = itemsToUpdate.find((item) => !sloten.has(item.id));
    let currentTime = firstFree?.fields?.Tijd ?? itemsToUpdate[0].fields?.Tijd ?? 28800;

    const updates = [];
    for (const item of itemsToUpdate) {
      const duration = (item.fields?.['Duur (minuten)'] || 15) * 60; // minuten → seconden
      if (sloten.has(item.id)) {
        currentTime = Math.max(currentTime, (item.fields?.Tijd || 0) + duration); // vast: tijd blijft, rest sluit erop aan
        continue;
      }
      const newTime = currentTime;
      currentTime = newTime + duration;
      if (newTime !== (item.fields?.Tijd || 0)) updates.push({ id: item.id, tijd: newTime });
    }

    if (updates.length === 0) { refreshData(); return; }

    try {
      // Batch update all items
      const response = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/batch-update-times`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update times');
      }
      
      refreshData();
    } catch (err) {
      console.error('Error recalculating times:', err);
      setError('Fout bij herberekenen tijden: ' + err.message);
    }
  };
  
  // Parkeer-check voor een locatie (PDOK + OSM + RDW via de backend).
  const checkParking = async (loc) => {
    const f = loc.fields || {};
    const address = f.Adres || [f.Straat, f.Postcode, f.Plaats].filter(Boolean).join(' ');
    if (!address) { setParking(prev => ({ ...prev, [loc.id]: { error: 'Geen adres ingevuld' } })); return; }
    setParkingLoading(prev => ({ ...prev, [loc.id]: true }));
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/parking/check?address=${encodeURIComponent(address)}`);
      const data = await res.json();
      setParking(prev => ({ ...prev, [loc.id]: res.ok ? data : { error: data.error || 'Check mislukt' } }));
    } catch (err) {
      setParking(prev => ({ ...prev, [loc.id]: { error: err.message } }));
    } finally {
      setParkingLoading(prev => ({ ...prev, [loc.id]: false }));
    }
  };

  // Zet het parkeeradvies in het 'Parkeren'-veld van de locatie (verschijnt op de PDF).
  const saveParkingNote = async (loc, text) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-locaties/${loc.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Parkeren: text }),
      });
      fetchCallsheet(false);
    } catch (err) { setError(err.message); }
  };

  // Delete location
  const deleteLocatie = async (locatieId) => {
    if (!(await bevestig({ titel: 'Locatie verwijderen?', tekst: 'Van deze callsheet.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-locaties/${locatieId}`, { method: 'DELETE' });
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Delete cast member
  const deleteCast = async (castId) => {
    if (!(await bevestig({ titel: 'Castlid verwijderen?', tekst: 'Van deze callsheet.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/callsheet-cast/${castId}`, { method: 'DELETE' });
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Fetch callsheets for copying planning
  const fetchCopyPlanningOptions = useCallback(async () => {
    setLoadingCopyOptions(true);
    try {
      // Get current project ID from callsheet data
      const projectId = callsheetData?.callsheet?.fields?.['Project (from DraaidagenOutlook)']?.[0] || '';
      const response = await authFetch(
        `${BACKEND_URL}/Api/callsheets-with-planning?projectId=${projectId}&search=${encodeURIComponent(copyPlanningSearch)}`
      );
      if (response.ok) {
        const data = await response.json();
        // Filter out current callsheet
        setCopyPlanningOptions(data.filter(cs => cs.id !== id));
      }
    } catch (err) {
      console.error('Error fetching copy options:', err);
    } finally {
      setLoadingCopyOptions(false);
    }
  }, [callsheetData, copyPlanningSearch, id]);

  // Open copy planning dialog
  const handleOpenCopyDialog = () => {
    setCopyPlanningDialog(true);
    fetchCopyPlanningOptions();
  };

  // Copy planning from selected callsheet
  const handleCopyPlanning = async (sourceCallsheetId) => {
    const confirmMsg = clearExistingPlanning 
      ? 'Planning kopiëren? De bestaande planning wordt eerst verwijderd.' 
      : 'Planning kopiëren? De items worden toegevoegd aan de bestaande planning.';
    
    if (!(await bevestig({ titel: 'Planning kopiëren?', tekst: confirmMsg, knop: 'Kopiëren' }))) return;
    
    setLoadingTemplate(true);
    setCopyPlanningDialog(false);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/copy-from`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetCallsheetId: id,
          sourceCallsheetId,
          clearExisting: clearExistingPlanning,
          includeCrew: copyIncludeCrew,
          includeCast: copyIncludeCast
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Kopiëren mislukt');

      console.log(`Copied ${result.created} planning, ${result.crewCreated || 0} crew, ${result.castCreated || 0} cast`);
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Load planning from template
  const handleLoadFromTemplate = async () => {
    if (!(await bevestig({ titel: 'Planning laden uit template?', tekst: 'Dit voegt standaard items toe aan de bestaande planning.', knop: 'Laden' }))) return;
    
    setLoadingTemplate(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheet-planning/from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callsheetId: id })
      });
      
      const result = await response.json();
      
      if (!response.ok) throw new Error(result.error || 'Template laden mislukt');
      
      console.log(`Created ${result.created} planning items from template`);
      fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTemplate(false);
    }
  };
  
  // Haalt de gegenereerde PDF op als blob (met Bearer-token) → { url, blob }.
  const fetchPdfBlob = async () => {
    const resp = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/download-pdf`);
    if (!resp.ok) throw new Error('PDF ophalen mislukt');
    const blob = await resp.blob();
    return { url: URL.createObjectURL(blob), blob };
  };

  const closePdfPreview = () => {
    setPdfPreview(prev => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: false, url: '', filename: '' };
    });
  };

  // Toon de PDF in een in-app preview-dialog (i.p.v. een nieuw tabblad dat in sommige
  // browsers direct downloadt i.p.v. previewt).
  const openPdfPreview = async () => {
    try {
      const { url } = await fetchPdfBlob();
      setPdfPreview({ open: true, url, filename: buildPdfFilename(callsheetData?.callsheet?.fields) });
    } catch (e) { setError(e.message); }
  };

  // "Opslaan als…": native Opslaan-als-dialoog via de File System Access API waar
  // beschikbaar (Chrome/Edge); anders fallback op een gewone download met nette naam.
  const savePdfAs = async () => {
    const filename = buildPdfFilename(callsheetData?.callsheet?.fields);
    try {
      const { url, blob } = await fetchPdfBlob();
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          URL.revokeObjectURL(url);
          return;
        } catch (pickErr) {
          if (pickErr?.name === 'AbortError') { URL.revokeObjectURL(url); return; } // gebruiker annuleerde
          // anders: val terug op gewone download hieronder
        }
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setError(e.message); }
  };

  // Generate PDF
  const handleGeneratePdf = async () => {
    // Vooraf-check: waarschuw voor ontbrekende velden vóór het genereren.
    const f = callsheetData?.callsheet?.fields || {};
    const crewList = callsheetData?.crew || [];
    const planningList = callsheetData?.planning || [];
    const locatieList = callsheetData?.locaties || [];
    const phoneOf = (c) => c.fields?.['Telefoon (handmatig)'] || c.fields?.['Telefoonnummer (from Personen)']?.[0] || c.fields?.Telefoonnummer;
    const missing = [];
    if (!(f['FullFrame Settelefoonnummer']?.[0] || f['Naam (from Contactpersoon Settelefoon)']?.[0])) missing.push('Settelefoon');
    if (!(f['Contactpersoon (handmatig)'] || f.Contactpersoon)) missing.push('Contactpersoon klant');
    if (!(f['Contactgegevens klant telefoon (Handmatig)'] || f['Telefoonnummer klant'])) missing.push('Telefoonnummer klant');
    if (locatieList.length === 0) missing.push('Geen locatie toegevoegd');
    const crewNoPhone = crewList.filter(c => !phoneOf(c)).length;
    if (crewNoPhone) missing.push(`${crewNoPhone} crewlid(en) zonder telefoonnummer`);
    const crewNoCall = crewList.filter(c => !c.fields?.Calltijd).length;
    if (crewNoCall) missing.push(`${crewNoCall} crewlid(en) zonder calltijd`);
    const planNoLoc = planningList.filter(p => !p.fields?.Locatie).length;
    if (planNoLoc) missing.push(`${planNoLoc} planning-item(s) zonder locatie`);
    // Parkeren: attendeer de PL als er locaties zijn zonder parkeerinfo.
    const locNoParking = locatieList.filter(l => !(l.fields?.Parkeren || '').trim()).length;
    if (locNoParking) missing.push(`${locNoParking} locatie(s) zonder parkeerinfo — check parkeren (knop bij de locatie)`);
    if (missing.length && !skipMissingWarnRef.current) {
      const proceed = await bevestig({
        titel: 'Mogelijk ontbreekt nog iets',
        tekst: `• ${missing.join('\n• ')}\n\nToch de PDF genereren? Je krijgt deze melding daarna niet meer deze sessie.`,
        knop: 'Toch genereren',
      });
      if (!proceed) return;
      // Eenmaal genegeerd → niet meer zeuren deze sessie.
      skipMissingWarnRef.current = true;
    }

    setGenerating(true);
    setPdfStatus('Bezig met genereren...');
    setError(null);

    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ includeWeather: true })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'PDF generatie mislukt');
      }
      
      if (result.pdfBase64) {
        setPdfStatus('PDF is gereed!');
        // PDF meteen in-app previewen (geen nieuw tabblad dat soms downloadt).
        // Downloaden/Opslaan-als kan vanuit de preview of via de losse knoppen.
        try {
          const { url } = await fetchPdfBlob();
          setPdfPreview({ open: true, url, filename: buildPdfFilename(callsheetData?.callsheet?.fields) });
        } catch (dlErr) { console.error('PDF preview error:', dlErr); }
      } else if (result.html) {
        setPdfStatus('HTML gegenereerd');
        // Open print preview
        const printWindow = window.open('', '_blank');
        printWindow.document.write(result.html);
        printWindow.document.close();
        printWindow.print();
      }
      
      await fetchCallsheet(false);
    } catch (err) {
      setError(err.message);
      setPdfStatus(`Fout: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };
  
  // Delete callsheet
  const handleDelete = async () => {
    if (!(await bevestig({ titel: 'Callsheet verwijderen?', tekst: 'De callsheet met crew, cast, locaties en planning wordt uit Airtable verwijderd.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Verwijderen mislukt');
      
      navigate(-1);
    } catch (err) {
      setError(err.message);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error && !callsheetData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/callsheets')}>Terug</Button>
      </Box>
    );
  }
  
  const { callsheet, crew, cast, planning, locaties: locatiesRaw, draaidag } = callsheetData || {};
  const fields = callsheet?.fields || {};
  // Draaidag als pure datum (YYYY-MM-DD) voor de reistijd in "Waar".
  const draaidagIso = String(fields['Datum (handmatig)'] || fields.Datum || '').slice(0, 10);
  // 'Projectleider (from Project)' is een linked-record lookup → geeft record-ID's terug.
  // Resolve naar naam via de personen-lijst (looksLikeAirtableId-patroon); val terug op niets
  // i.p.v. een rauwe recXXX te tonen.
  const projectleiderRaw = draaidag?.[0]?.fields?.['Projectleider (from Project)'];
  const projectleider = (() => {
    const arr = Array.isArray(projectleiderRaw) ? projectleiderRaw : (projectleiderRaw ? [projectleiderRaw] : []);
    const names = arr.map(v => {
      if (typeof v === 'string' && /^rec[A-Za-z0-9]{14}$/.test(v)) {
        const found = personen.find(p => p.id === v);
        return found?.fields?.['Volledige naam'] || found?.fields?.Naam || null;
      }
      return v;
    }).filter(Boolean);
    return names.length ? names.join(', ') : null;
  })();

  // Projectnummer (+ -naam) uit de gekoppelde draaidag/project — tonen in de header.
  const projectnummer = (() => {
    const raw = draaidag?.[0]?.fields?.['Projectnummer (from Project)'];
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v != null && v !== '' ? String(v) : null;
  })();
  const projectnaam = (() => {
    const raw = draaidag?.[0]?.fields?.['Projectnr_en_naam (from Project)']
      || draaidag?.[0]?.fields?.['Naam (from Project)'];
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v != null && v !== '' ? String(v) : null;
  })();

  // Sort locaties so the first location used in planning (by time) appears first
  // This reduces errors by making the first shoot location prominent
  const sortedPlanningForLocaties = (planning || []).slice().sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));
  const locationFirstUsage = new Map();
  sortedPlanningForLocaties.forEach(p => {
    const locName = (p.fields?.Locatie || '').toLowerCase().trim();
    if (locName && !locationFirstUsage.has(locName)) {
      locationFirstUsage.set(locName, p.fields?.Tijd || 0);
    }
  });
  // Volgorde = "in welke volgorde rij je ernaartoe" (Joris, 7 sep): handmatig gezette
  // Volgorde wint, daarna het eerste planning-item dat de locatie noemt.
  const locaties = (locatiesRaw || []).slice().sort((a, b) => {
    const va = a.fields?.Volgorde ?? Infinity;
    const vb = b.fields?.Volgorde ?? Infinity;
    if (va !== vb) return va - vb;
    const nameA = (a.fields?.['Naam locatie'] || '').toLowerCase().trim();
    const nameB = (b.fields?.['Naam locatie'] || '').toLowerCase().trim();
    const timeA = locationFirstUsage.get(nameA) ?? Infinity;
    const timeB = locationFirstUsage.get(nameB) ?? Infinity;
    return timeA - timeB;
  });
  const verplaatsLocatie = (index, richting) => {
    const nieuw = [...locaties];
    const doel = index + richting;
    if (doel < 0 || doel >= nieuw.length) return;
    [nieuw[index], nieuw[doel]] = [nieuw[doel], nieuw[index]];
    persistRowOrder(nieuw, 'locaties', 'callsheet-locaties');
  };
  
  // Editable field component
  const editCtx = { editingField, editValue, setEditValue, updateField, setEditingField };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper sx={{ p: 0, mb: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {/* Kop in de stijl van de rest van de app: rustig en licht (Joris 28 sep) — geen verloop meer. */}
        <Box sx={{ color: '#1f2937', p: 3, pb: 2, borderLeft: '4px solid #c7d2fe' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1' }}>Callsheet</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<BackIcon />} onClick={() => navigate('/callsheets')} sx={{ textTransform: 'none', color: '#475569' }}>
                Terug
              </Button>
              <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => setDuplicateTarget(callsheet)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Dupliceren
              </Button>
              <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={handleDelete} sx={{ textTransform: 'none' }}>
                Verwijder
              </Button>
            </Box>
          </Box>
          <EditableField
            label="Titel"
            fieldName="Titel"
            value={fields.Titel}
            dark
            ctx={editCtx}
          />
          <Typography variant="subtitle1" sx={{ mt: 0.5, color: '#475569' }}>
            {formatDate(fields['Datum (handmatig)'] || fields.Datum)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, mt: 2, pt: 2, borderTop: '1px solid #eef0f3', flexWrap: 'wrap' }}>
            {projectnummer && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Project</Typography>
                <Typography variant="body2">{projectnaam || `#${projectnummer}`}</Typography>
                {projectnaam && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>#{projectnummer}</Typography>}
              </Box>
            )}
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Settelefoon</Typography>
              <Typography variant="body2">{fields['Naam (from Contactpersoon Settelefoon)']?.[0] || '-'}</Typography>
              {fields['FullFrame Settelefoonnummer']?.[0] && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{telefoonNl(fields['FullFrame Settelefoonnummer'])}</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Contactpersoon klant</Typography>
              <Typography variant="body2">{fields['Contactpersoon (handmatig)'] || fields.Contactpersoon || '-'}</Typography>
              {telefoonNl(fields['Telefoonnummer klant'] || fields['Contactgegevens klant telefoon (Handmatig)']) && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  {telefoonNl(fields['Telefoonnummer klant'] || fields['Contactgegevens klant telefoon (Handmatig)'])}
                </Typography>
              )}
            </Box>
            {projectleider && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Projectleider</Typography>
                <Typography variant="body2">{Array.isArray(projectleider) ? projectleider.join(', ') : projectleider}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Locatie</Typography>
              <Typography variant="body2">{(() => { const l = (locaties || [])[0]?.fields || {}; return [l['Naam locatie'], l.Plaats].filter(Boolean).join(', ') || '-'; })()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Tijden</Typography>
              <Typography variant="body2">{extra.begintijd || extra.eindtijd ? `${extra.begintijd || '?'} – ${extra.eindtijd || '?'}` : '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', fontWeight: 700 }}>Crew</Typography>
              <Typography variant="body2">{(crew || []).length || '-'}{briefingTekst ? ' · briefing ✓' : ''}</Typography>
            </Box>
          </Box>
          {(callsheet?.fields?.['Project (from DraaidagenOutlook)']?.[0] || projectnummer) && (
            <KlantVereistenBlok variant="detail" sx={{ mb: 0 }}
              projectId={callsheet?.fields?.['Project (from DraaidagenOutlook)']?.[0]} nummer={projectnummer} />
          )}
        </Box>
        {weather && (
          <Box sx={{ px: 3, py: 1.5, backgroundColor: '#f0f9ff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#0369a1' }}>🌤️ {weather}</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mx: 3, my: 2 }}>{error}</Alert>}
      </Paper>
      
      {/* Basis: locatie + begin/eind — het eerste wat een crewlid wil weten (levend callsheet) */}
      {(() => {
        const eersteLoc = (locatiesRaw || [])[0];
        const locTekst = eersteLoc ? [eersteLoc.fields?.['Naam locatie'], eersteLoc.fields?.Straat, eersteLoc.fields?.Plaats].filter(Boolean).join(' · ') : '';
        const planningGesorteerd = [...(planning || [])].filter((p) => p.fields?.Tijd != null).sort((a, b) => (a.fields.Tijd || 0) - (b.fields.Tijd || 0));
        const uitPlanning = () => {
          if (!planningGesorteerd.length) return;
          const begin = planningGesorteerd[0].fields.Tijd || 0;
          const eind = Math.max(...planningGesorteerd.map((p) => (p.fields.Tijd || 0) + ((p.fields['Duur (minuten)'] || 0) * 60)));
          bewaarExtra({ begintijd: formatDuration(begin), eindtijd: formatDuration(eind), tijdenBron: 'planning' });
        };
        return (
          <Paper sx={{ p: 2.5, mb: 3, border: '1px solid #e5e7eb', borderLeft: '4px solid #f59e0b' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Locatie</Typography>
                {locTekst ? (
                  <Typography sx={{ fontWeight: 600 }}>{locTekst}{(locatiesRaw || []).length > 1 ? ` (+${locatiesRaw.length - 1})` : ''}</Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#b45309', fontWeight: 600 }}>Nog geen locatie</Typography>
                    <Button size="small" variant="outlined" onClick={() => setLocatieDialog({ open: true, data: null })} sx={{ textTransform: 'none' }}>Locatie toevoegen</Button>
                  </Box>
                )}
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField label="Begintijd" type="time" size="small" fullWidth value={extra.begintijd || ''}
                  onChange={(e) => setExtra((x) => ({ ...x, begintijd: e.target.value }))}
                  onBlur={(e) => { if ((e.target.value || '') !== '' || extra.begintijd === '') bewaarExtra({ begintijd: e.target.value, tijdenBron: 'handmatig' }); }}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField label="Eindtijd" type="time" size="small" fullWidth value={extra.eindtijd || ''}
                  onChange={(e) => setExtra((x) => ({ ...x, eindtijd: e.target.value }))}
                  onBlur={(e) => bewaarExtra({ eindtijd: e.target.value, tijdenBron: 'handmatig' })}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Tooltip title={planningGesorteerd.length ? 'Begin = eerste planningsregel, eind = laatste regel + duur' : 'Vul eerst de planning in'}>
                  <span>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={uitPlanning} disabled={!planningGesorteerd.length} sx={{ textTransform: 'none' }}>
                      Tijden uit planning
                    </Button>
                  </span>
                </Tooltip>
                {extra.tijdenBron === 'planning' && <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>Gesynchroniseerd met de planning</Typography>}
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Tooltip title="Laat de crew weten dat de callsheet is bijgewerkt (melding in intern, crew-app en/of e-mail)">
                <span>
                  <Button size="small" variant="contained" disableElevation disabled={!(crew || []).length}
                    onClick={() => setInformeer({ bericht: '', webapp: true, crewApp: true, mail: false, bezig: false })}
                    sx={{ textTransform: 'none', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                    📣 Crew informeren
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Paper>
        );
      })()}

      <CallsheetBijlagen callsheetId={id} onOverzicht={setBijlagenOverzicht} />

      {/* PDF Generation */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <PdfIcon sx={{ fontSize: 28, color: '#dc2626' }} />
          <Typography variant="h6" fontWeight={600}>PDF</Typography>
          {pdfStatus && (
            <Chip 
              label={pdfStatus} 
              color={pdfStatus.includes('gereed') || pdfStatus.includes('Gereed') ? 'success' : pdfStatus.includes('Bezig') ? 'warning' : 'default'}
              size="small"
            />
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Preview + Opslaan-als (via authFetch met Bearer-token) — alleen als er al een PDF is */}
            {callsheetData?.callsheet?.fields?.['PDF-bestand']?.[0]?.url && (
              <>
                <Button variant="outlined" startIcon={<PreviewIcon />} onClick={openPdfPreview}>
                  Preview
                </Button>
                <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={savePdfAs}>
                  Opslaan als…
                </Button>
              </>
            )}
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
              onClick={handleGeneratePdf}
              disabled={generating}
            >
              {generating ? 'Bezig...' : 'Genereer nieuwe PDF'}
            </Button>
            <WordExportKnop callsheetId={id} fields={callsheetData?.callsheet?.fields} />
            <IconButton onClick={fetchCallsheet} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        {generating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              PDF generatie kan even duren... (tot 30 sec)
            </Typography>
          </Box>
        )}
        {callsheetData?.callsheet?.fields?.StatusPDFWarning && (
          <Alert severity={callsheetData.callsheet.fields.StatusPDFWarning.includes('Fout') ? 'error' : 'info'} sx={{ mt: 2 }}>
            {callsheetData.callsheet.fields.StatusPDFWarning}
          </Alert>
        )}
      </Paper>

      <Dialog open={Boolean(informeer) && !verzendCheck} onClose={() => !informeer?.bezig && setInformeer(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Crew informeren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Naar {(crew || []).length} crewleden: {(crew || []).map((c) => c.fields?.['Volledige naam (from Personen)']?.[0] || c.fields?.Persoon || '?').filter(Boolean).join(', ')}
          </Typography>
          <TextField fullWidth multiline minRows={2} size="small" label="Bericht (optioneel)"
            placeholder="De callsheet is bijgewerkt — bekijk de laatste versie."
            value={informeer?.bericht || ''} onChange={(e) => setInformeer((x) => ({ ...x, bericht: e.target.value }))} sx={{ mb: 1.5 }} />
          <FormControlLabel control={<Checkbox checked={Boolean(informeer?.webapp)} onChange={(e) => setInformeer((x) => ({ ...x, webapp: e.target.checked }))} />}
            label="Melding in intern (belletje)" />
          <FormControlLabel control={<Checkbox checked={Boolean(informeer?.crewApp)} onChange={(e) => setInformeer((x) => ({ ...x, crewApp: e.target.checked }))} />}
            label="Update in de FF Crew-app" />
          <FormControlLabel control={<Checkbox checked={Boolean(informeer?.mail)} onChange={(e) => setInformeer((x) => ({ ...x, mail: e.target.checked }))} />}
            label="E-mail naar de crew" />
          <BijlagenInDialoog overzicht={bijlagenOverzicht} mail={Boolean(informeer?.mail)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInformeer(null)} disabled={informeer?.bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" disableElevation onClick={() => setVerzendCheck(true)} disabled={informeer?.bezig || !(informeer?.webapp || informeer?.crewApp || informeer?.mail) || Boolean(informeer?.mail && bijlagenProbleem(bijlagenOverzicht))} sx={{ textTransform: 'none' }}>
            {informeer?.bezig ? 'Bezig…' : 'Versturen'}
          </Button>
        </DialogActions>
      </Dialog>
      <CallsheetVerzendCheck
        open={Boolean(informeer) && verzendCheck && !informeer?.bezig}
        feiten={feitenUitCallsheet({ fields, draaidag, extra, locaties, crew, personen })}
        kanalen={informeer || {}}
        aantalBijlagen={aantalBijlagen(bijlagenOverzicht)}
        laadOntvangers={laadCrewOntvangers}
        onTerug={() => setVerzendCheck(false)}
        onVerstuur={(bevestigd) => { setVerzendCheck(false); verstuurInformeer(bevestigd); }} />
      <DuplicateCallsheetDialog callsheet={duplicateTarget} onClose={() => setDuplicateTarget(null)} />
      <Dialog open={!!aiBriefing} onClose={() => setAiBriefing(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          ✨ Voorstel uit het project
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1.5, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setAiBronnenOpen((v) => !v)}>
            {(aiBriefing?.bronnen || []).length} bronnen gelezen {aiBronnenOpen ? '▾' : '▸'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {aiBronnenOpen && (
            <Box sx={{ mb: 2, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0', maxHeight: 160, overflow: 'auto' }}>
              {(aiBriefing?.bronnen || []).map((b) => (
                <Typography key={b.ref} variant="caption" sx={{ display: 'block', color: '#475569' }}>
                  · {b.url ? <a href={b.url} target="_blank" rel="noreferrer" style={{ color: '#4338ca' }}>{b.label}</a> : b.label} <span style={{ color: '#94a3b8' }}>[{b.ref}]</span>
                </Typography>
              ))}
            </Box>
          )}

          {(aiBriefing?.conflicten || []).length > 0 && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid #fcd34d', bgcolor: '#fffbeb' }}>
              <Typography fontWeight={800} sx={{ mb: 0.5 }}>Bronnen spreken elkaar tegen</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Het voorstel hieronder gebruikt per punt de aangevinkte versie (meestal de nieuwste bron). Klopt dat, dan hoef je hier niets te doen.
                Klopt het niet: kies de juiste versie en klik onderaan op “Opnieuw met mijn keuzes”.
              </Typography>
              {aiBriefing.conflicten.map((c, i) => (
                <Box key={i} sx={{ mb: 1.25 }}>
                  <Typography variant="body2" fontWeight={700}>{c.onderwerp}</Typography>
                  {c.versies.map((v, j) => (
                    <FormControlLabel key={j} sx={{ display: 'flex', alignItems: 'flex-start', ml: 0, mr: 0, mb: 0.25 }}
                      control={<Radio size="small" checked={aiConflictKeuze[i] === v.waarde} onChange={() => setAiConflictKeuze((p) => ({ ...p, [i]: v.waarde }))} sx={{ py: 0.25 }} />}
                      label={<Typography variant="body2"><b>{v.waarde}</b> <Typography component="span" variant="caption" color="text.secondary">— {v.bron}{v.datum && v.datum !== '?' ? `, ${v.datum}` : ''}</Typography></Typography>} />
                  ))}
                  {!aiConflictStart[i] && <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>Niet zeker welke versie het voorstel gebruikt: {c.gekozen || '?'} — kies zelf.</Typography>}
                  {c.reden && <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>Waarom: {c.reden}</Typography>}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
            {[['Callsheet (productie)', ['algemene_opmerkingen', 'props', 'styling', 'visagie', 'lunch', 'reminder', 'kledingvoorschriften']], ['Briefing crew', ['briefing_crew']]].map(([label, keys], idx) => {
              const n = keys.filter((k) => aiKeuze[k]).length + (idx === 0 ? Object.keys(aiExtraKeuze).length : 0);
              return (
                <Button key={label} size="small" variant={aiTab === idx ? 'contained' : 'outlined'} disableElevation onClick={() => setAiTab(idx)}
                  sx={{ textTransform: 'none', fontWeight: 700, ...(aiTab === idx ? { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } } : { borderColor: '#c4b5fd', color: '#5b21b6' }) }}>
                  {label}{n ? ` · ${n}` : ''}
                </Button>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {aiTab === 0 ? 'Productieniveau: tijden, locatie, contact, wie wat meeneemt. Staat op het callsheet dat ook de klant ziet.' : 'Achtergrond voor cameraman en regisseur: verhaal, shots, referenties met links. Eigen document, niet voor de klant.'}
          </Typography>

          {Object.entries(aiKeuze).filter(([k]) => (aiTab === 1) === (k === 'briefing_crew')).map(([k, v]) => (
            <Box key={k} sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: v.aan ? '#c4b5fd' : '#e2e8f0', bgcolor: v.aan ? '#faf5ff' : '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Checkbox size="small" checked={!!v.aan} onChange={(e) => setAiKeuze((p) => ({ ...p, [k]: { ...p[k], aan: e.target.checked } }))} sx={{ p: 0.5 }} />
                <Typography fontWeight={700} variant="body2">{AI_LABELS[k] || k}</Typography>
                {v.bestaand && <Chip size="small" label="vervangt bestaande tekst" sx={{ height: 18, fontSize: 10.5, bgcolor: '#fef3c7', color: '#92400e' }} />}
              </Box>
              {k === 'briefing_crew' && !aiBriefingBewerk ? (
                <Box>
                  <Box sx={{ maxHeight: 420, overflow: 'auto', px: 1, py: 0.5, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, '& h1, & h2, & h3': { fontSize: '0.92rem', fontWeight: 800, mt: 1.5, mb: 0.5 }, '& ul': { pl: 2.5, my: 0.5 }, '& li': { mb: 0.3, lineHeight: 1.5, fontSize: '0.86rem' }, '& p': { lineHeight: 1.55, fontSize: '0.86rem', my: 0.5 }, '& a': { color: '#4338ca', fontSize: '0.78rem', textDecoration: 'none', bgcolor: '#eef2ff', borderRadius: 0.75, px: 0.5 } }}>
                    <ReactMarkdown components={mdLinkComponents}>{metRegeleinden(bronRefsNaarLinks(v.tekst, aiBriefing?.bronnen || []))}</ReactMarkdown>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Bronnen staan als klikbare labels; de crew en de PDF zien ze niet.</Typography>
                    <Button size="small" onClick={() => setAiBriefingBewerk(true)} sx={{ textTransform: 'none' }}>Tekst bewerken</Button>
                  </Box>
                </Box>
              ) : (k === 'briefing_crew' || k === 'algemene_opmerkingen') ? (
                <CallsheetTekstEditor value={v.tekst} minHeight={k === 'briefing_crew' ? 260 : 100}
                  onChange={(t) => setAiKeuze((p) => ({ ...p, [k]: { ...p[k], tekst: t } }))} />
              ) : (
                <TextField fullWidth multiline minRows={2} maxRows={10} size="small" value={v.tekst}
                  onChange={(e) => setAiKeuze((p) => ({ ...p, [k]: { ...p[k], tekst: e.target.value } }))} inputProps={{ dir: 'ltr', style: { textAlign: 'left', fontSize: 13.5, lineHeight: 1.5 } }} />
              )}
              {k === 'briefing_crew' && aiBriefingBewerk && <Button size="small" onClick={() => setAiBriefingBewerk(false)} sx={{ textTransform: 'none', mt: 0.5 }}>Opgemaakt tonen</Button>}
            </Box>
          ))}

          {aiTab === 0 && Object.keys(aiExtraKeuze).length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ook uit de bronnen gehaald</Typography>
              {[
                aiExtraKeuze.locatie && ['locatie', '📍 Locatie', [aiExtraKeuze.locatie.naam, aiExtraKeuze.locatie.straat, [aiExtraKeuze.locatie.postcode, aiExtraKeuze.locatie.plaats].filter(Boolean).join(' '), aiExtraKeuze.locatie.opmerkingen].filter(Boolean).join(' · '), (callsheetData?.locaties || []).length ? 'komt als extra locatie' : null],
                aiExtraKeuze.settelefoon && ['settelefoon', '📞 Settelefoon', aiExtraKeuze.settelefoon.naam, aiExtraKeuze.settelefoon.persoon ? `koppelt ${aiExtraKeuze.settelefoon.persoon.fields?.['Volledige naam'] || ''}` : 'niet gevonden in Personen — kies hieronder handmatig'],
                aiExtraKeuze.contactKlant && ['contactKlant', '👤 Contactpersoon klant', [aiExtraKeuze.contactKlant.naam, aiExtraKeuze.contactKlant.telefoon].filter(Boolean).join(' · '), null],
                aiExtraKeuze.planning && ['planning', '🕐 Planning', aiExtraKeuze.planning.rows.map((r) => `${r.tijd} ${r.omschrijving}`).join(' · '), null],
              ].filter(Boolean).map(([k, titel, tekst, hint]) => (
                <Box key={k} sx={{ mb: 1, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: aiExtraKeuze[k].aan ? '#c4b5fd' : '#e2e8f0', bgcolor: aiExtraKeuze[k].aan ? '#faf5ff' : '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Checkbox size="small" checked={!!aiExtraKeuze[k].aan} disabled={k === 'settelefoon' && !aiExtraKeuze.settelefoon.persoon}
                      onChange={(e) => setAiExtraKeuze((p) => ({ ...p, [k]: { ...p[k], aan: e.target.checked } }))} sx={{ p: 0.5 }} />
                    <Typography fontWeight={700} variant="body2">{titel}</Typography>
                    {hint && <Chip size="small" label={hint} sx={{ height: 18, fontSize: 10.5, bgcolor: '#f1f5f9', color: '#475569' }} />}
                  </Box>
                  <Typography variant="body2" sx={{ ml: 4.5, color: '#334155' }}>{tekst}</Typography>
                  {k === 'planning' && (callsheetData?.planning || []).length > 0 && (
                    <FormControlLabel sx={{ ml: 3.5 }} control={<Checkbox size="small" checked={!!aiExtraKeuze.planning.vervang} onChange={(e) => setAiExtraKeuze((p) => ({ ...p, planning: { ...p.planning, vervang: e.target.checked } }))} />}
                      label={<Typography variant="caption">Bestaande planning ({(callsheetData?.planning || []).length} regels) vervangen</Typography>} />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {(aiBriefing?.vragen || []).length > 0 && aiTab === 0 && (
            <Alert severity="info" sx={{ mt: 1, mb: 1.5 }}>
              <b>Nog checken bij de klant</b>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{aiBriefing.vragen.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </Alert>
          )}

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography fontWeight={800} variant="body2" sx={{ mb: 1 }}>Bijsturen</Typography>
            <TextField fullWidth multiline minRows={2} size="small" label="Aanwijzingen voor de AI" placeholder="Bijv. “Jef stopt om 15:15, planning in callsheet is oud” of “Gert komt niet, laat weg”." value={aiAanwijzingen} onChange={(e) => setAiAanwijzingen(e.target.value)} sx={{ mb: 1 }} inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }} />
            <TextField fullWidth multiline minRows={2} maxRows={10} size="small" label="Extra tekst als bron (mail, WhatsApp, notulen)" placeholder="Plak hier bijvoorbeeld een doorgestuurde mail die nog niet in het project staat." value={aiExtraBron} onChange={(e) => setAiExtraBron(e.target.value)} inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1 }}>
              {Object.keys(aiConflictKeuze).some((i) => aiConflictKeuze[i] !== aiConflictStart[i]) && (
                <Typography variant="caption" color="text.secondary">Je koos een andere versie — maak het voorstel opnieuw om die te gebruiken.</Typography>
              )}
              <Button size="small" variant="outlined" disabled={aiBriefingBusy} onClick={() => vraagAiBriefing({ opnieuw: true })}
                startIcon={aiOpnieuwBezig ? <CircularProgress size={13} /> : null} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {aiOpnieuwBezig ? 'Bezig…' : 'Opnieuw met mijn keuzes en aanwijzingen'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiBriefing(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          {aiOpnieuwBezig && <Box sx={{ flex: 1, pl: 1 }}><AiVoortgang job={ai.job} compact onStop={ai.stop} /></Box>}
          <Button variant="contained" disableElevation onClick={neemAiOver} disabled={aiOpnieuwBezig} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {aiAantalAan} onderdeel{aiAantalAan === 1 ? '' : 'en'} overnemen
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {/* Callsheet Info */}
        {/* ✨ AI-voorbereiding — eigen blok (Joris 18 sep: hoort niet onder Algemene opmerkingen) */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #ddd6fe', bgcolor: '#faf5ff' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Typography variant="h6" fontWeight={700}>✨ Voorbereiden met AI</Typography>
                <Typography variant="body2" color="text.secondary">
                  Leest de notities, meetings, mails en het dossier van dit project en stelt voor: algemene opmerkingen en productievelden voor het callsheet, plus een aparte briefing voor de crew. Tegenstrijdige bronnen krijg je voorgelegd; jij kiest wat je overneemt.
                </Typography>
              </Box>
              <Button variant="contained" disableElevation onClick={() => vraagAiBriefing()} disabled={aiBriefingBusy}
                startIcon={ai.starten ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
                {aiBriefingBusy ? 'AI bereidt voor…' : 'Voorbereiden met AI'}
              </Button>
              {briefingTekst && <Button variant="outlined" onClick={() => navigate(`/callsheets/${id}/briefing`)} sx={{ textTransform: 'none' }}>Briefing openen</Button>}
              {ai.job && !aiOpnieuwBezig && (
                <Box sx={{ flexBasis: '100%' }}>
                  <AiVoortgang job={ai.job} ingevuld={aiIngevuld} voorstelAantal={aiVoorstelLijst.length} punten={aiPunten}
                    fouten={aiInvulFouten} onStop={ai.stop} onVoorstel={bekijkAiVoorstel} onHerstel={herstelAiBlok}
                    onSluit={() => { ai.sluit(); setAiIngevuld([]); setAiVoorstelBlokken([]); setAiInvulFouten([]); }} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Callsheet: settelefoon + contact klant (PDF-kop) */}
        <Grid item data-sectie="contact" xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>📋 Callsheet</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {/* Kies een contactpersoon uit de Bedrijven-tabel → vult naam + telefoon. */}
              <KlantContactPicker
                currentLabel={fields['Contactpersoon (handmatig)'] || fields.Contactpersoon || ''}
                onSelect={async (c) => {
                  const patch = {
                    'Contactpersoon (handmatig)': c.naam,
                    'Contactgegevens klant telefoon (Handmatig)': c.telefoon || '',
                  };
                  // Optimistisch alleen deze 2 velden bijwerken — geen volledige paginarefresh.
                  setCallsheetData(prev => prev ? {
                    ...prev,
                    callsheet: { ...prev.callsheet, fields: { ...prev.callsheet.fields, ...patch } },
                  } : prev);
                  try {
                    await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(patch),
                    });
                  } catch (err) { setError(err.message); fetchCallsheet(false); }
                }}
              />

              {/* …of handmatig invullen — voor contacten die (nog) niet in Bedrijven staan.
                  Staat bewust direct onder de zoek-picker zodat het meteen duidelijk is. */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 1 }}>
                Staat de contactpersoon niet in Bedrijven? Vul 'm hieronder handmatig in.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={6}>
                  <EditableField label="Contactpersoon klant" fieldName="Contactpersoon (handmatig)" value={fields['Contactpersoon (handmatig)']} ctx={editCtx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <EditableField label="Telefoonnummer klant" fieldName="Contactgegevens klant telefoon (Handmatig)" value={fields['Contactgegevens klant telefoon (Handmatig)']} ctx={editCtx} />
                </Grid>
              </Grid>

              {/* Settelefoon — kies de Full Frame-persoon; nummer wordt opgezocht. */}
              <SettelefoonPicker
                personen={personen}
                currentLabel={fields['Naam (from Contactpersoon Settelefoon)']?.[0] || fields['FullFrame Settelefoonnummer']?.[0] || ''}
                onSelect={async (p) => {
                  const naam = p.fields?.['Volledige naam'] || p.fields?.Naam || '';
                  const telRaw = p.fields?.Telefoonnummer;
                  const tel = Array.isArray(telRaw) ? telRaw[0] : (telRaw || '');
                  // Optimistisch bijwerken (incl. de opgezochte naam/nummer) → geen herlaad.
                  setCallsheetData(prev => prev ? {
                    ...prev,
                    callsheet: { ...prev.callsheet, fields: { ...prev.callsheet.fields,
                      'Contactpersoon Settelefoon': [p.id],
                      'Naam (from Contactpersoon Settelefoon)': naam ? [naam] : [],
                      'FullFrame Settelefoonnummer': tel ? [tel] : [],
                    } },
                  } : prev);
                  try {
                    await authFetch(`${BACKEND_URL}/Api/callsheets/${id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ 'Contactpersoon Settelefoon': [p.id] }),
                    });
                    fetchCallsheet(false);   // stille reconcile (geen laad-indicator)
                  } catch (err) { setError(err.message); fetchCallsheet(false); }
                }}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <EditableField label="Datum" fieldName="Datum (handmatig)" value={fields['Datum (handmatig)']} type="date" ctx={editCtx} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Algemene opmerkingen */}
        <Grid item data-sectie="opmerkingen" xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>📝 Algemene opmerkingen</Typography>
              <Divider sx={{ mb: 2 }} />
              {/* Opmaak-editor (kopjes, vet, lijsten) — wiki-feedback 26 sep; opslag blijft tekst (markdown). */}
              <OpmaakVeld
                value={fields['Algemene opmerkingen'] || ''}
                onSave={(v) => updateField('Algemene opmerkingen', v)}
                placeholder="Tijden, locatie, contact ter plaatse, wat er opgeleverd wordt…"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Productie & opmerkingen (Block C): elk veld apart; leeg = niet in de PDF. */}
        <Grid item data-sectie="productie" xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>🎬 Productie & opmerkingen</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Wordt automatisch voorgevuld vanuit de draaidag/briefing waar mogelijk. Laat een veld leeg om het uit de PDF te houden.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {[
                  { fieldName: 'Props', label: '📦 Props' },
                  { fieldName: 'Styling', label: '🎨 Styling' },
                  { fieldName: 'Visagie', label: '💄 Visagie & haar' },
                  { fieldName: 'Lunch', label: '🍴 Lunch / catering' },
                  { fieldName: 'Reminder', label: '⏰ Reminder' },
                  { fieldName: 'Kledingvoorschriften', label: '👕 Kledingvoorschriften' },
                ].map(f => (
                  <Grid item xs={12} md={6} key={f.fieldName}>
                    <EditableField label={f.label} fieldName={f.fieldName} value={fields[f.fieldName]} multiline ctx={editCtx} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Locaties */}
        <Grid item data-sectie="locaties" xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationIcon sx={{ color: '#ef4444' }} />
                <Typography variant="h6" fontWeight={600}>Locaties</Typography>
                <Chip label={locaties?.length || 0} size="small" sx={{ ml: 'auto' }} />
                <Button size="small" startIcon={<AddIcon />} onClick={() => setLocatieDialog({ open: true, data: null })}>
                  Toevoegen
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {locaties && locaties.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {locaties.map((loc, locIndex) => {
                    const pr = parking[loc.id];
                    const v = pr?.verdict;
                    const pColor = v?.level === 'onsite' ? '#16a34a' : v?.level === 'paid' ? '#d97706' : '#dc2626';
                    const pBg = v?.level === 'onsite' ? '#dcfce7' : v?.level === 'paid' ? '#fef3c7' : '#fee2e2';
                    return (
                    <Paper key={loc.id} sx={{ p: 1.5, backgroundColor: '#f9fafb' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{locIndex + 1}. {loc.fields?.['Naam locatie'] || 'Locatie'}</Typography>
                          <Typography variant="body2" color="text.secondary">{loc.fields?.Adres || loc.fields?.Straat}</Typography>
                          {loc.fields?.Plaats && <Typography variant="caption" sx={{ display: 'block' }}>{loc.fields.Plaats}</Typography>}
                          <ReistijdRegel
                            adres={loc.fields?.Adres || [loc.fields?.Straat, [loc.fields?.Postcode, loc.fields?.Plaats].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                            datum={draaidagIso} aankomst={extra.begintijd} />
                          {loc.fields?.Opmerkingen && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#475569', whiteSpace: 'pre-wrap' }}>
                              💬 {loc.fields.Opmerkingen}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Tooltip title="Eerder aandoen"><span><IconButton size="small" disabled={locIndex === 0} onClick={() => verplaatsLocatie(locIndex, -1)}>▲</IconButton></span></Tooltip>
                          <Tooltip title="Later aandoen"><span><IconButton size="small" disabled={locIndex === locaties.length - 1} onClick={() => verplaatsLocatie(locIndex, 1)}>▼</IconButton></span></Tooltip>
                          <IconButton size="small" onClick={() => setLocatieDialog({ open: true, data: loc })}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => deleteLocatie(loc.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      </Box>

                      {/* Locatiefoto's: zodat crew ziet waar ze moeten zijn (ingang, laadperron…). */}
                      <Box sx={{ mt: 1 }}>
                        <FotoStrook api={fotoApi} doel={{ locatieId: loc.id }} label="Foto's van de locatie" />
                      </Box>

                      {/* Parkeren — auto-check (kan ik hier parkeren / betaald / garage in de buurt). */}
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e5e7eb' }}>
                        {(() => {
                          const lf = loc.fields || {};
                          const adres = lf.Adres || [lf.Straat, lf.Postcode, lf.Plaats].filter(Boolean).join(' ');
                          if (!adres) return null;
                          // Google Maps 'parkeren'-zoekopdracht bij de locatie → zelf de situatie checken (satelliet/streetview).
                          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`parkeren ${adres}`)}`;
                          return (
                            <Button size="small" component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer"
                              startIcon={<LocationIcon fontSize="small" />}
                              sx={{ textTransform: 'none', mb: 0.5, display: 'inline-flex', width: 'fit-content' }}>
                              Parkeren op Google Maps bekijken
                            </Button>
                          );
                        })()}
                        {loc.fields?.Parkeren && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#0f172a' }}>
                            <ParkingIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} />
                            {loc.fields.Parkeren}
                          </Typography>
                        )}
                        {loc.fields?.Parkeerfoto && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                            {String(loc.fields.Parkeerfoto).split('\n').map(s => s.trim()).filter(Boolean).map((url) => (
                              <Box key={url} component="a" href={url} target="_blank" rel="noopener noreferrer">
                                <Box component="img" src={url} alt="parkeerfoto"
                                  sx={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 0.5, border: '1px solid #e5e7eb' }} />
                              </Box>
                            ))}
                          </Box>
                        )}
                        {!pr ? (
                          <Button size="small" startIcon={<ParkingIcon fontSize="small" />}
                            onClick={() => checkParking(loc)} disabled={parkingLoading[loc.id]}
                            sx={{ textTransform: 'none' }}>
                            {parkingLoading[loc.id] ? 'Parkeren checken…' : 'Parkeren checken'}
                          </Button>
                        ) : pr.error ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="error">Parkeer-check: {pr.error}</Typography>
                            <Button size="small" onClick={() => checkParking(loc)} sx={{ textTransform: 'none' }}>Opnieuw</Button>
                          </Box>
                        ) : (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip size="small" icon={<ParkingIcon sx={{ fontSize: 14, color: `${pColor} !important` }} />}
                                label={v?.label || 'Parkeren'} sx={{ color: pColor, bgcolor: pBg, fontWeight: 600 }} />
                              {pr.nearestGarage && (
                                <Typography variant="caption" color="text.secondary">
                                  Garage: {pr.nearestGarage.name} ~{pr.nearestGarage.distanceM} m
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#374151' }}>{v?.message}</Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                              <Button size="small" variant="outlined" onClick={() => saveParkingNote(loc, v?.message || '')} sx={{ textTransform: 'none' }}>Opslaan bij locatie</Button>
                              <Button size="small" onClick={() => checkParking(loc)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Opnieuw checken</Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">Geen locaties</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Crew */}
        <Grid item data-sectie="crew" xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PeopleIcon sx={{ color: '#10b981' }} />
                <Typography variant="h6" fontWeight={600}>Crew</Typography>
                <Chip label={crew?.length || 0} size="small" sx={{ ml: 'auto' }} />
                {crew?.length > 0 && (callsheetData?.planning || []).length > 0 && (
                  <Tooltip title="Zet calltijd van alle crew gelijk aan de eerste call-tijd van de dag">
                    <Button size="small" startIcon={<ScheduleIcon />} onClick={setAllCrewToFirstCalltime}>
                      Calltijd = 1e
                    </Button>
                  </Tooltip>
                )}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setCrewDialog({ open: true, data: null })}>
                  Toevoegen
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {crew && crew.length > 0 ? (
                (() => {
                  const sortedCrew = sortByVolgorde(crew);
                  return (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                      <TableCell width={28}></TableCell>
                      <TableCell><strong>Naam</strong></TableCell>
                      <TableCell><strong>Functie</strong></TableCell>
                      <TableCell><strong>Calltijd</strong></TableCell>
                      <TableCell width={80}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCrew.map((c) => {
                      const tel = telefoonNl(c.fields?.['Telefoon (handmatig)'] || c.fields?.['Telefoonnummer (from Personen)']?.[0] || c.fields?.Telefoonnummer || '');
                      const freelancer = !c.fields?.Personen?.length && persoonOpNaam(c.fields?.Persoon, personen)?.fields?.Type === 'Freelancer';
                      return (
                      <TableRow key={c.id} hover
                        onDragOver={(e) => { if (draggedRow?.key === 'crew') e.preventDefault(); }}
                        onDrop={() => handleRowDrop('crew', 'callsheet-crew', sortedCrew, c.id)}
                        sx={{ opacity: draggedRow?.id === c.id ? 0.4 : 1 }}>
                        <TableCell sx={{ px: 0.5, cursor: 'grab', color: '#94a3b8' }}
                          draggable
                          onDragStart={() => setDraggedRow({ key: 'crew', id: c.id })}
                          onDragEnd={() => setDraggedRow(null)}>
                          <DragIcon fontSize="small" />
                        </TableCell>
                        <TableCell>
                          {c.fields?.Persoon || '-'}
                          {(tel || freelancer) && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
                              {[freelancer ? 'Freelancer' : '', tel].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{c.fields?.['Functie (handmatig)'] || '-'}</TableCell>
                        <TableCell>{formatDuration(c.fields?.Calltijd)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setCrewDialog({ open: true, data: c })}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => deleteCrew(c.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                  );
                })()
              ) : (
                <Typography variant="body2" color="text.secondary">Geen crew toegevoegd</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cast */}
        <Grid item data-sectie="cast" xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ color: '#8b5cf6' }} />
                <Typography variant="h6" fontWeight={600}>Cast &amp; klant/aanwezigen</Typography>
                <Chip label={cast?.length || 0} size="small" sx={{ ml: 'auto' }} />
                {cast?.length > 0 && (callsheetData?.planning || []).length > 0 && (
                  <Tooltip title="Zet calltijd van alle cast gelijk aan de eerste call-tijd van de dag">
                    <Button size="small" startIcon={<ScheduleIcon />} onClick={setAllCastToFirstCalltime}>
                      Calltijd = 1e
                    </Button>
                  </Tooltip>
                )}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setCastDialog({ open: true, data: null })}>
                  Toevoegen
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {cast && cast.length > 0 ? (
                (() => {
                  const sortedCast = sortByVolgorde(cast);
                  return (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                      <TableCell width={28}></TableCell>
                      <TableCell><strong>Naam</strong></TableCell>
                      <TableCell><strong>Rol</strong></TableCell>
                      <TableCell><strong>Calltijd</strong></TableCell>
                      <TableCell width={80}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCast.map((c) => (
                      <TableRow key={c.id} hover
                        onDragOver={(e) => { if (draggedRow?.key === 'cast') e.preventDefault(); }}
                        onDrop={() => handleRowDrop('cast', 'callsheet-cast', sortedCast, c.id)}
                        sx={{ opacity: draggedRow?.id === c.id ? 0.4 : 1 }}>
                        <TableCell sx={{ px: 0.5, cursor: 'grab', color: '#94a3b8' }}
                          draggable
                          onDragStart={() => setDraggedRow({ key: 'cast', id: c.id })}
                          onDragEnd={() => setDraggedRow(null)}>
                          <DragIcon fontSize="small" />
                        </TableCell>
                        <TableCell>{c.fields?.Naam || c.fields?.['Naam (from Cast)']?.[0] || c.fields?.['Cast (handmatig)'] || '-'}</TableCell>
                        <TableCell>{c.fields?.Rol || '-'}</TableCell>
                        <TableCell>{formatDuration(c.fields?.Calltijd)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setCastDialog({ open: true, data: c })}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => deleteCast(c.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                  );
                })()
              ) : (
                <Typography variant="body2" color="text.secondary">Geen cast toegevoegd</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Planning */}
        <Grid item data-sectie="planning" xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon sx={{ color: '#f59e0b' }} />
                <Typography variant="h6" fontWeight={600}>Planning</Typography>
                <Chip label={planning?.length || 0} size="small" sx={{ ml: 'auto' }} />
                <Tooltip title="Kopieer planning van andere draaidag">
                  <Button 
                    size="small" 
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenCopyDialog}
                    disabled={loadingTemplate}
                    sx={{ mr: 1 }}
                  >
                    {loadingTemplate ? <CircularProgress size={16} /> : '📋 Kopieer van draaidag'}
                  </Button>
                </Tooltip>
                <Tooltip title="Laad standaard planning items vanuit template">
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={handleLoadFromTemplate}
                    disabled={loadingTemplate}
                    sx={{ mr: 1 }}
                  >
                    📄 Template
                  </Button>
                </Tooltip>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setPlanningDialog({ open: true, data: null })}>
                  Toevoegen
                </Button>
                {planning && planning.length > 1 && (
                  <Tooltip title="Herbereken alle tijden op basis van duur. Regels met een 🔒 vastgezette begintijd blijven staan (handig om parallel te plannen).">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CalculateIcon />}
                      onClick={() => recalculatePlanningTimes()}
                      sx={{ ml: 1 }}
                    >
                      Herbereken tijden
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {planning && planning.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                      <TableCell width={60}><strong>#</strong></TableCell>
                      <TableCell><strong>Tijd</strong></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Duur</strong></TableCell>
                      <TableCell><strong>Omschrijving</strong></TableCell>
                      <TableCell><strong>Locatie</strong></TableCell>
                      <TableCell><strong>Cast</strong></TableCell>
                      <TableCell><strong>Opmerkingen</strong></TableCell>
                      <TableCell><strong>Foto's</strong></TableCell>
                      <TableCell width={130}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...planning].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0)).map((p, index, arr) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => movePlanningItem(p.id, 'up')}
                              disabled={index === 0}
                              sx={{ p: 0.25 }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => movePlanningItem(p.id, 'down')}
                              disabled={index === arr.length - 1}
                              sx={{ p: 0.25 }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap', bgcolor: lockedRows.has(p.id) ? '#fff7ed' : undefined }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Tooltip title={lockedRows.has(p.id) ? 'Begintijd staat vast — blijft staan bij herberekenen; wat erna komt sluit hierop aan. Klik om vrij te geven.' : 'Begintijd vastzetten (bv. lunch of een afspraak met de klant): blijft staan bij herberekenen, de rest sluit erop aan'}>
                              <IconButton size="small" onClick={() => toggleLock(p.id)} sx={{ p: 0.25, color: lockedRows.has(p.id) ? '#ea580c' : '#cbd5e1' }}>
                                {lockedRows.has(p.id) ? <LockIcon sx={{ fontSize: 15 }} /> : <LockOpenIcon sx={{ fontSize: 15 }} />}
                              </IconButton>
                            </Tooltip>
                            {renderPlanningCell(p, 'Tijd', formatDuration(p.fields?.Tijd) || '-')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {renderPlanningCell(p, 'Duur (minuten)', p.fields?.['Duur (minuten)'] ? `${p.fields['Duur (minuten)']} min` : '-')}
                        </TableCell>
                        <TableCell>{renderPlanningCell(p, 'Omschrijving', p.fields?.Omschrijving || '-')}</TableCell>
                        <TableCell>
                          <PlanningCombo
                            value={p.fields?.Locatie || ''}
                            options={locaties.map(l => l.fields?.['Naam locatie'] || l.fields?.Naam || '').filter(Boolean)}
                            onCommit={(v) => setPlanningField(p.id, 'Locatie', v)}
                            placeholder="Locatie"
                          />
                        </TableCell>
                        <TableCell>
                          <PlanningCastMulti
                            value={p.fields?.Cast || ''}
                            options={(cast || []).map(c => c.fields?.Naam || c.fields?.['Naam (from Cast)']?.[0] || c.fields?.['Cast (handmatig)'] || '').filter(Boolean)}
                            onCommit={(v) => setPlanningField(p.id, 'Cast', v)}
                            placeholder="Cast"
                          />
                        </TableCell>
                        <TableCell>{renderPlanningCell(p, 'Opmerkingen', p.fields?.Opmerkingen || '-')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <PlanningFotoCel api={fotoApi} regel={p} locaties={locaties} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setPlanningDialog({ open: true, data: p })}><EditIcon fontSize="small" /></IconButton>
                          <Tooltip title="Regel dupliceren"><IconButton size="small" onClick={() => duplicatePlanning(p)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                          <IconButton size="small" onClick={() => deletePlanning(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">Geen planning toegevoegd</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Briefing crew: het verhaal voor cameraman/regisseur (PG callsheet_extra.briefing). */}
        <Grid item data-sectie="briefing" xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>🎥 Briefing crew</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Doel van de film, wat er zeker gefilmd moet worden, stijl, valkuilen. Eigen document voor de crew (PDF, concept-mail); bronverwijzingen zie jij alleen.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {briefingBewerk || !extra.briefing ? (
                <CallsheetTekstEditor value={briefingTekst} onChange={setBriefingTekst} minHeight={180}
                  placeholder="Nog geen briefing. Klik op ‘Voorbereiden met AI’ hierboven, of schrijf hem zelf." />
              ) : (
                <Box sx={{ px: 0.5, '& h1, & h2, & h3': { fontSize: '0.95rem', fontWeight: 800, mt: 2, mb: 0.5 }, '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 }, '& ul': { pl: 2.5, my: 0.5 }, '& li': { mb: 0.35, lineHeight: 1.55, fontSize: '0.9rem' }, '& p': { lineHeight: 1.6, fontSize: '0.9rem' }, '& a': { color: '#4338ca', wordBreak: 'break-all' } }}>
                  <ReactMarkdown components={mdLinkComponents}>{metRegeleinden(bronnenTonen ? bronRefsNaarLinks(briefingTekst, extra.briefingBronnen || []) : stripBronRefs(briefingTekst))}</ReactMarkdown>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {extra.briefing && !briefingBewerk && (
                  <FormControlLabel sx={{ mr: 'auto', ml: 0 }} control={<Switch size="small" checked={bronnenTonen} onChange={(e) => setBronnenTonen(e.target.checked)} />}
                    label={<Typography variant="caption" color="text.secondary">Bronnen tonen (alleen voor jou; crew en PDF zien ze niet)</Typography>} />
                )}
                {extra.briefing && !briefingBewerk && (
                  <Button size="small" variant="text" startIcon={<PdfIcon />} disabled={briefingActie === 'pdf'} onClick={downloadBriefingPdf} sx={{ textTransform: 'none' }}>
                    {briefingActie === 'pdf' ? 'PDF maken…' : 'PDF'}
                  </Button>
                )}
                {extra.briefing && !briefingBewerk && (
                  <Button size="small" variant="text" disabled={briefingActie === 'mail'} onClick={conceptMailCrew} sx={{ textTransform: 'none' }}>
                    {briefingActie === 'mail' ? 'Concept maken…' : '✉️ Concept-mail crew'}
                  </Button>
                )}
                {extra.briefing && !briefingBewerk && (
                  <Button size="small" variant="text" onClick={naarShotlist} sx={{ textTransform: 'none' }}>🎬 Shotlist maken</Button>
                )}
                <Button size="small" variant="text" onClick={() => navigate(`/callsheets/${id}/briefing`)} sx={{ textTransform: 'none' }}>
                  Openen als document
                </Button>
                {extra.briefing && !briefingBewerk && (
                  <Button size="small" variant="outlined" onClick={() => setBriefingBewerk(true)} sx={{ textTransform: 'none' }}>Bewerken</Button>
                )}
                {(briefingBewerk || !extra.briefing) && (
                  <Button size="small" variant="contained" disableElevation disabled={briefingBezig || briefingTekst === (extra.briefing || '')}
                    onClick={async () => { setBriefingBezig(true); const ok = await bewaarExtra({ briefing: briefingTekst }); setBriefingBezig(false); if (ok) { setBriefingBewerk(false); meldCorrecties({ briefing_crew: briefingTekst }); } }}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {briefingBezig ? 'Opslaan…' : 'Briefing opslaan'}
                  </Button>
                )}
                {briefingBewerk && <Button size="small" onClick={() => { setBriefingTekst(extra.briefing || ''); setBriefingBewerk(false); }} sx={{ textTransform: 'none' }}>Annuleren</Button>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Dialogs */}
      <CrewDialog 
        open={crewDialog.open} 
        onClose={() => setCrewDialog({ open: false, data: null })}
        onSave={refreshData}
        initialData={crewDialog.data}
        callsheetId={id}
        personen={personen}
      />
      <CastDialog 
        open={castDialog.open} 
        onClose={() => setCastDialog({ open: false, data: null })}
        onSave={refreshData}
        initialData={castDialog.data}
        callsheetId={id}
        castingOptions={castingOptions}
      />
      <PlanningDialog 
        open={planningDialog.open} 
        onClose={() => setPlanningDialog({ open: false, data: null })}
        onSave={refreshData}
        onAfterSave={refreshDataAndRecalculate}
        initialData={planningDialog.data}
        callsheetId={id}
      />
      <LocatieDialog 
        open={locatieDialog.open} 
        onClose={() => setLocatieDialog({ open: false, data: null })}
        onSave={refreshData}
        initialData={locatieDialog.data}
        callsheetId={id}
        existingLocaties={existingLocaties}
      />

      {/* PDF-preview (in-app, i.p.v. nieuw tabblad) */}
      <Dialog open={pdfPreview.open} onClose={closePdfPreview} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <PdfIcon sx={{ color: '#dc2626' }} />
          <Typography variant="h6" component="span">PDF-preview</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pdfPreview.filename}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={savePdfAs}>
              Opslaan als…
            </Button>
            <IconButton onClick={closePdfPreview}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {pdfPreview.url && (
            <Box component="iframe" title="PDF-preview" src={pdfPreview.url}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Copy Planning Dialog */}
      <Dialog 
        open={copyPlanningDialog} 
        onClose={() => setCopyPlanningDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📋 Kopieer planning van andere draaidag
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={clearExistingPlanning}
                  onChange={(e) => setClearExistingPlanning(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Verwijder bestaande planning eerst
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {clearExistingPlanning
                      ? 'De huidige planning wordt vervangen door de gekopieerde items'
                      : 'De gekopieerde items worden toegevoegd aan de bestaande planning'}
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={copyIncludeCrew} onChange={(e) => setCopyIncludeCrew(e.target.checked)} />}
                label={<Typography variant="body2">Ook crew meekopiëren</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={copyIncludeCast} onChange={(e) => setCopyIncludeCast(e.target.checked)} />}
                label={<Typography variant="body2">Ook cast meekopiëren</Typography>}
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Zoek op titel of project..."
            value={copyPlanningSearch}
            onChange={(e) => setCopyPlanningSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchCopyPlanningOptions()}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={fetchCopyPlanningOptions}>
                  Zoeken
                </Button>
              )
            }}
          />
          
          {loadingCopyOptions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Same Project Section */}
              {copyPlanningOptions.filter(cs => cs.isSameProject).length > 0 && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    🎬 Zelfde project
                  </Typography>
                  {copyPlanningOptions.filter(cs => cs.isSameProject).map(cs => (
                    <Paper 
                      key={cs.id} 
                      sx={{ 
                        p: 2, mb: 1, cursor: 'pointer', 
                        border: '2px solid #e5e7eb',
                        '&:hover': { borderColor: '#3b82f6', backgroundColor: '#f0f9ff' }
                      }}
                      onClick={() => handleCopyPlanning(cs.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={600}>{cs.titel}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {cs.projectName} • {cs.datum ? new Date(cs.datum).toLocaleDateString('nl-NL') : 'Geen datum'}
                          </Typography>
                        </Box>
                        <Chip label={`${cs.planningCount} items`} size="small" color="primary" />
                      </Box>
                    </Paper>
                  ))}
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              
              {/* Other Projects Section */}
              {copyPlanningOptions.filter(cs => !cs.isSameProject).length > 0 && (
                <>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                    📁 Andere projecten
                  </Typography>
                  {copyPlanningOptions.filter(cs => !cs.isSameProject).slice(0, 20).map(cs => (
                    <Paper 
                      key={cs.id} 
                      sx={{ 
                        p: 2, mb: 1, cursor: 'pointer',
                        border: '1px solid #e5e7eb',
                        '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' }
                      }}
                      onClick={() => handleCopyPlanning(cs.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={500}>{cs.titel}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {cs.projectName} • {cs.datum ? new Date(cs.datum).toLocaleDateString('nl-NL') : 'Geen datum'}
                          </Typography>
                        </Box>
                        <Chip label={`${cs.planningCount} items`} size="small" variant="outlined" />
                      </Box>
                    </Paper>
                  ))}
                </>
              )}
              
              {copyPlanningOptions.length === 0 && (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                  Geen callsheets met planning gevonden
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyPlanningDialog(false)}>Annuleren</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CallSheetManager;
